import React, { useState, useMemo } from 'react';
import ProgramAnalysisFifaTab from './ProgramAnalysisFifaTab';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Skeleton,
    Collapse,
    IconButton,
    CircularProgress,
    Avatar,
    Tabs,
    Tab,
    Slider,
    Menu,
    Autocomplete,
    TextField,
    Snackbar,
    Switch,
    FormControlLabel,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Stack,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    TrendingUp as TrendingUpIcon,
    MoreVert as MoreVertIcon,
    YouTube as YouTubeIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    BarChart as BarChartIcon,
    LinkedIn as LinkedInIcon,
    OpenInNew as OpenInNewIcon,
    Image as ImageIcon,
    ContentCopy as ContentCopyIcon,
    Search as SearchIcon,
    Send as SendIcon,
} from '@mui/icons-material';
import { Button } from '@mui/material';
import { convertToCSV, downloadCSV, createTimestampedFilename } from '../utils/csvUtils';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import apiService from '../services/api';
import type { PodcastTaxonomyFilters } from '../services/api';
import { ROUTES_NAMES } from '../components/Layout';
import { RANKING_PARAM, RANKING_TYPES, buildSocialPostCopy, buildSocialPostRankingLines, isExcludedLivestreamRankingChannelName, isExplorerLivestreamRankingParam, isWorldCupRankingParam, rankingStepIndexForParam, type RankingParam, type SocialPostRankingItem } from '../components/ranking-card';
import type { DailyMetric } from '../types';
import { getS3Url, ensureDirectS3Url, getProgramImageUrl, getPodcastImageUrl } from '../utils/imageUtils';
import { getDisplayDaySincePublished } from '../utils/dailyMetricsDisplayDay';
import { BRAND_CATEGORIES, BRAND_CATEGORY_LABELS } from '../constants/brandCategories';
import { getLivestreamRankingDateRange } from '../utils/livestreamRankingDateRange';
import { RankingDimensionStepper } from '../components/ranking-card';
import {
    aggregateProgramsToChannelAnalysisRows,
    buildChannelsViewsRankingRows,
    buildChannelsInteractionsRankingRows,
    countChannelsFromPrograms,
    sortChannelRows,
    type ChannelAnalysisData,
} from './ProgramAnalysis/top10Aggregate';
import { getDefaultProgramMetricsDateRange } from './ProgramAnalysis/dailyMetricsChart';
import BrandMergeDialog, { type BrandMergeSource } from '../components/BrandMergeDialog';
import {
    PROGRAM_ANALYSIS_ROUTE_CATEGORY,
    PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE,
    PROGRAM_ANALYSIS_ROUTE_TAXONOMY,
    matchesProgramAnalysisRouteTaxonomy,
} from '../constants/programAnalysisTaxonomy';

const RANKING_MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/** Comunidad dimension: grouping (channel vs live program) × category (all vs digital). */
const COMMUNITY_RANKING_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: `${RANKING_PARAM.livestreamPrograms}|all`, label: 'All channels' },
    { value: `${RANKING_PARAM.livestreamPrograms}|digital`, label: 'Digital channels' },
    { value: `${RANKING_PARAM.livestreamProgramsProgram}|all`, label: 'All livestream programs' },
    { value: `${RANKING_PARAM.livestreamProgramsProgram}|digital`, label: 'Digital only livestream programs' },
];

/** Weekly Instagram posts: digital rankings for the period selected on this page. */
const WEEKLY_IG_COPY_STORAGE_KEY = 'podscope.weeklyIgCopy.v2';

const WEEKLY_IG_COPY_CLOSING = `💡 Más insights del ecosistema en Radar Podscope.

Fuente: Podscope, inteligencia y verificación en medios digitales.`;

const WEEKLY_IG_RANKINGS: {
    param: RankingParam;
    label: string;
    countPlaceholder: '{programCount}' | '{channelCount}';
    copy: string;
}[] = [
    {
        param: 'views',
        label: 'Programas por views promedio',
        countPlaceholder: '{programCount}',
        copy: `Top 10 programas por views promedio en el ecosistema de conversación en YouTube Chile, de {programCount} programas activos entre {dateRange}.

Solo consideramos videos de más de 5 minutos, medidos al día 7 de publicación.

${WEEKLY_IG_COPY_CLOSING}`,
    },
    {
        param: 'channels-views',
        label: 'Canales por views totales',
        countPlaceholder: '{channelCount}',
        copy: `Top 10 canales digitales por views totales en el ecosistema de conversación en YouTube Chile, de {channelCount} canales entre {dateRange}.

Solo consideramos videos de más de 5 minutos, medidos al día 7 de publicación.

${WEEKLY_IG_COPY_CLOSING}`,
    },
    {
        param: 'interactions',
        label: 'Programas por interacción promedio',
        countPlaceholder: '{programCount}',
        copy: `Top 10 programas por interacción promedio en el ecosistema de conversación en YouTube Chile, de {programCount} programas activos entre {dateRange}.

Solo consideramos likes y comentarios de videos de más de 5 minutos, medidos al día 7 de publicación.

${WEEKLY_IG_COPY_CLOSING}`,
    },
    {
        param: 'channels-interactions',
        label: 'Canales por interacción total',
        countPlaceholder: '{channelCount}',
        copy: `Top 10 canales digitales por interacción total en el ecosistema de conversación en YouTube Chile, de {channelCount} canales entre {dateRange}.

Solo consideramos likes y comentarios de videos de más de 5 minutos, medidos al día 7 de publicación.

${WEEKLY_IG_COPY_CLOSING}`,
    },
    {
        param: 'livestream-programs-program',
        label: 'Programas live por espectadores promedio',
        countPlaceholder: '{programCount}',
        copy: `Top 10 programas digitales por espectadores promedio en vivo en el ecosistema de conversación en YouTube Chile, de {programCount} programas de canales 100% digitales entre {dateRange}.

Ya sean estrenos o livestreams, ordenados por espectadores en vivo promedio en todos los streams durante el periodo. Solo consideramos livestreams de más de 15 minutos.

${WEEKLY_IG_COPY_CLOSING}`,
    },
    {
        param: 'programs-brands',
        label: 'Programas por marcas',
        countPlaceholder: '{programCount}',
        copy: `Top 10 programas por marcas en el ecosistema de conversación en YouTube Chile, de {programCount} programas activos entre {dateRange}.

Contamos marcas distintas con mención, en créditos, regalos o placements, en episodios de más de 5 minutos. En empate, se prioriza por marcas con menciones en contenido, luego views y luego marcas repetidas de la semana anterior. División por tipo de mención disponible en Podscope.

${WEEKLY_IG_COPY_CLOSING}`,
    },
];

function loadWeeklyIgCopyDrafts(): Partial<Record<RankingParam, string>> {
    try {
        const raw = localStorage.getItem(WEEKLY_IG_COPY_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Partial<Record<RankingParam, string>>;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function formatWeeklyIgDateRange(params: {
    week?: string;
    startDate?: string;
    endDate?: string;
}): string {
    const formatPair = (start: Date, end: Date) => {
        const d1 = start.getDate();
        const d2 = end.getDate();
        const month1 = RANKING_MONTHS_ES[start.getMonth()]?.toLowerCase() ?? '';
        const month2 = RANKING_MONTHS_ES[end.getMonth()]?.toLowerCase() ?? '';
        if (month1 === month2) return `el ${d1} y ${d2} de ${month1}`;
        return `el ${d1} de ${month1} y ${d2} de ${month2}`;
    };
    if (params.week) {
        const weekStr = params.week.split(',')[0];
        if (weekStr && weekStr !== 'all') {
            const start = new Date(`${weekStr}T12:00:00`);
            if (!isNaN(start.getTime())) {
                const end = new Date(start);
                end.setDate(end.getDate() + 6);
                return formatPair(start, end);
            }
        }
    }
    if (params.startDate && params.endDate) {
        const start = new Date(`${params.startDate}T12:00:00`);
        const end = new Date(`${params.endDate}T12:00:00`);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) return formatPair(start, end);
    }
    return 'el período seleccionado';
}

function fillWeeklyIgCopy(
    template: string,
    values: { dateRange: string; programCount: string; channelCount: string },
): string {
    return template
        .replaceAll('{dateRange}', values.dateRange)
        .replaceAll('{programCount}', values.programCount)
        .replaceAll('{channelCount}', values.channelCount);
}

/** Copa Mundial dimension: YouTube programs vs livestream program grouping. */
const WORLD_CUP_RANKING_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: RANKING_PARAM.worldCupViews, label: 'Programas YouTube por views (Copa Mundial)' },
    { value: RANKING_PARAM.worldCupLivestreamPrograms, label: 'Programas live por espectadores (Copa Mundial)' },
];

type BrandChannelMentionsPanelProps = {
    brandId: string;
    brandName: string;
    podcastId: string;
    podcastTitle: string;
    week?: string;
    year?: string;
    startDate?: string;
    endDate?: string;
    taxonomy: PodcastTaxonomyFilters;
    onDeleteMention: (mentionId: string, brandId: string, podcastId: string) => void;
    deletePending?: boolean;
};

function BrandChannelMentionsPanel({
    brandId,
    brandName,
    podcastId,
    podcastTitle,
    week,
    year,
    startDate,
    endDate,
    taxonomy,
    onDeleteMention,
    deletePending = false,
}: BrandChannelMentionsPanelProps) {
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            'brand-analytics-channel-mentions',
            brandId,
            podcastId,
            week,
            year,
            startDate,
            endDate,
            taxonomy,
        ],
        queryFn: () =>
            apiService.getBrandAnalyticsChannelMentions(
                brandId,
                week,
                taxonomy,
                podcastId,
                year,
                startDate,
                endDate,
            ),
    });

    const mentions = data?.mentions ?? [];

    return (
        <Box sx={{ ml: 4, mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
                Mentions for &quot;{brandName}&quot; in {podcastTitle}
            </Typography>
            {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                        Loading mentions…
                    </Typography>
                </Box>
            ) : isError ? (
                <Typography variant="body2" color="error">
                    Failed to load mentions
                </Typography>
            ) : mentions.length > 0 ? (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Content</TableCell>
                                <TableCell>Program</TableCell>
                                <TableCell align="center">Confidence</TableCell>
                                <TableCell>Link</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mentions.map((mention) => (
                                <TableRow key={mention._id}>
                                    <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                            {mention.contentTitle || 'N/A'}
                                        </Typography>
                                        {mention.contentPublishedAt && (
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(mention.contentPublishedAt).toLocaleDateString()}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {mention.programName || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2">
                                            {mention.confidence ? `${mention.confidence}%` : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {mention.contentUrl && (
                                            <Link
                                                href={
                                                    mention.segmentStart !== undefined &&
                                                    mention.segmentStart !== null
                                                        ? `${mention.contentUrl}${mention.contentUrl.includes('?') ? '&' : '?'}t=${Math.floor(mention.segmentStart)}`
                                                        : mention.contentUrl
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <YouTubeIcon fontSize="small" />
                                            </Link>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            disabled={deletePending}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const shouldSkipConfirm = e.metaKey || e.ctrlKey;
                                                if (
                                                    shouldSkipConfirm ||
                                                    window.confirm(
                                                        `Are you sure you want to delete this mention for "${mention.contentTitle || 'N/A'}"?`,
                                                    )
                                                ) {
                                                    onDeleteMention(String(mention._id), brandId, podcastId);
                                                }
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No mentions found
                </Typography>
            )}
        </Box>
    );
}

interface ProgramAnalysisData {
    _id: string;
    name: string;
    imageS3Key?: string;
    imageUpdatedAt?: string;
    updatedAt?: string;
    podcast: {
        _id: string;
        title: string;
        imageS3Key?: string;
        imageUrl?: string;
        imageUpdatedAt?: string;
        updatedAt?: string;
        socialAccounts?: { instagram?: string; tiktok?: string };
        socialMetrics?: {
            youtube?: { subscriberCount?: number };
            instagram?: { followerCount?: number };
            tiktok?: { followerCount?: number };
        };
    };
    /** Count of episodes with YouTube duration over 5 min in the selected period. */
    contentCount: number;
    /** Latest dailyMetrics views summed for long-form episodes (fallback day7 / insights). */
    totalViews: number;
    /** Same latest-snapshot view sum for long-form (TOTAL VIEWS column). */
    totalViewsLongForm?: number;
    totalLikes: number;
    totalComments: number;
    totalInteractions: number;
    /** Avg views per long-form episode (latest daily snapshot per video). */
    avgViews: number;
    /** Mean per-episode views at ~7 days after publish (same basis as guests 7D views). */
    avgViewsDay7: number;
    /** Sum of per-episode views at ~7 days after publish. */
    totalViewsDay7?: number;
    avgViewsAllTime: number;
    /** Avg interactions per long-form episode (latest daily snapshot). */
    avgInteractions: number;
    /** Mean likes+comments per episode at ~7 days after publish. */
    avgInteractionsDay7: number;
    /** Sum of likes+comments at ~7 days after publish across long-form episodes. */
    totalInteractionsDay7?: number;
    engagementRate: number;
    uniquePaidBrandsCount: number;
    /** Paid mentions excluding curator-rejected feedback. */
    paidMentionsCount?: number;
    /** Distinct content with ≥1 paid, non-rejected mention. */
    contentWithMentionsCount?: number;
    totalDurationMinutes?: number;
    dateRange: {
        earliest: string;
        latest: string;
    };
    rankingPosition?: number; // Backend ranking position (based on totalViews)
    positionLastWeek?: number | null;
    avgViewsLastWeek?: number | null; // Average views from previous week (7-day average)
    isPodcast?: boolean; // Filter out programs that are the main podcast
}

interface ContentWithMetrics {
    _id: string;
    title: string;
    publishedAt?: string;
    views?: number;
    likes?: number;
    comments?: number;
    dailyMetrics?: DailyMetric[];
    selectedMetricDate?: string;
    isPodcast?: boolean;
    url?: string;
    source?: string;
    insights?: {
        views?: number;
        likes?: number;
        comments?: number;
        shares?: number;
        saves?: number;
        reach?: number;
        streams?: number;
        uniqueListeners?: number;
        averageListenPct?: number;
        impressions?: number;
        followersGained?: number;
        interactions?: number;
        collectedAt?: string | Date;
        [key: string]: any;
    };
}

// Component for channel brands section
const ChannelBrandsSection: React.FC<{ podcastId: string; week?: string; podcastTitle: string; formatNumber: (num: number) => string }> = ({ podcastId, week, podcastTitle, formatNumber }) => {
    const { data: brandsData, isLoading: brandsLoading } = useQuery({
        queryKey: ['channel-brands', podcastId, week],
        queryFn: () => apiService.getChannelBrands(podcastId, week),
        enabled: !!podcastId
    });

    if (brandsLoading) {
        return (
            <Box sx={{ mt: 2 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 1 }} />
            </Box>
        );
    }

    const brands = brandsData?.brands || [];

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                Brands from {podcastTitle} ({brands.length} {brands.length === 1 ? 'brand' : 'brands'})
            </Typography>
            {brands.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Brand</TableCell>
                                <TableCell>Categoría</TableCell>
                                <TableCell align="right">Mentions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {brands.map((brand: any) => (
                                <TableRow key={brand._id}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {brand.brandS3Key && (
                                                <Avatar
                                                    src={ensureDirectS3Url(getS3Url(brand.brandS3Key))}
                                                    sx={{ width: 24, height: 24 }}
                                                >
                                                    {!brand.brandS3Key && brand.brandName.charAt(0).toUpperCase()}
                                                </Avatar>
                                            )}
                                            <Typography variant="body2">{brand.brandName}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {brand.brandCategory ? (BRAND_CATEGORY_LABELS[brand.brandCategory] || brand.brandCategory) : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatNumber(brand.mentionCount)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No brands found
                </Typography>
            )}
        </Box>
    );
};

// Program Edit Dialog Component
const ProgramEditDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    contentId: string;
    contentTitle: string;
    currentProgramId: string | undefined;
    programs: { _id: string; name: string }[];
    onSave: (contentId: string, programId: string | null) => Promise<void>;
    saveInProgress: boolean;
}> = ({ open, onClose, contentId, contentTitle, currentProgramId, programs, onSave, saveInProgress }) => {
    const [selectedProgramId, setSelectedProgramId] = useState<string>('');

    // Reset state when dialog opens
    React.useEffect(() => {
        if (open) {
            setSelectedProgramId(currentProgramId || '');
        }
    }, [open, currentProgramId]);

    const handleSave = () => {
        const programId = selectedProgramId === '' ? null : selectedProgramId;
        onSave(contentId, programId);
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit Program: {contentTitle}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select a program from the list below. All programs for this podcast channel are available.
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Program</InputLabel>
                        <Select
                            value={selectedProgramId}
                            label="Program"
                            onChange={(e) => setSelectedProgramId(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>No Program</em>
                            </MenuItem>
                            {programs.map(program => (
                                <MenuItem key={program._id} value={program._id}>
                                    {program.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {programs.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                            No programs available for this podcast channel.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={saveInProgress}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={saveInProgress || selectedProgramId === (currentProgramId || '')}
                >
                    {saveInProgress ? (
                        <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Updating...
                        </>
                    ) : (
                        'Update Program'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Component for expandable program row
const ProgramRow: React.FC<{
    program: ProgramAnalysisData;
    index: number;
    isExpanded: boolean;
    onToggleExpand: (programId: string, event: React.MouseEvent) => void;
    onProgramClick: (program: ProgramAnalysisData, event: React.MouseEvent) => void;
    formatNumber: (num: number) => string;
    selectedWeeks?: string[];
    programBrandsDateQuery?: { week?: string; startDate?: string; endDate?: string };
    isSingleWeekFilter?: boolean;
    isClickable?: boolean;
}> = ({ program, index, isExpanded, onToggleExpand, onProgramClick, formatNumber, selectedWeeks = ['all'], programBrandsDateQuery = {}, isSingleWeekFilter = false, isClickable = true }) => {
    // Fetch content for this program when expanded
    const { data: contentData, isLoading: contentLoading } = useQuery({
        queryKey: ['program-content', program._id, program.podcast._id, selectedWeeks],
        queryFn: () => {
            // If 'all' is selected or no weeks selected, pass undefined
            // Otherwise, pass comma-separated weeks
            const hasAll = selectedWeeks.includes('all');
            const weekParam = hasAll || selectedWeeks.length === 0
                ? undefined
                : selectedWeeks.join(',');

            return apiService.getPodcastContentAnalysis(
                program.podcast._id,
                1,
                200, // Get up to 100 content items
                undefined,
                'publishedAt',
                'desc',
                weekParam,
                program._id
            );
        },
        enabled: isExpanded
    });

    // Fetch brands for this program when expanded
    const { data: brandsData, isLoading: brandsLoading } = useQuery({
        queryKey: [
            'program-brands',
            program._id,
            programBrandsDateQuery.week,
            programBrandsDateQuery.startDate,
            programBrandsDateQuery.endDate,
        ],
        queryFn: () => apiService.getProgramBrands(program._id, programBrandsDateQuery),
        enabled: isExpanded
    });

    const content: ContentWithMetrics[] = contentData?.content || [];
    const brands = brandsData?.brands || [];
    const queryClient = useQueryClient();

    // State for program edit dialog
    const [programEditDialogOpen, setProgramEditDialogOpen] = useState(false);
    const [programEditContentId, setProgramEditContentId] = useState<string>('');
    const [programEditContentTitle, setProgramEditContentTitle] = useState<string>('');
    const [programEditCurrentProgramId, setProgramEditCurrentProgramId] = useState<string | undefined>(undefined);
    const [programEditSaveInProgress, setProgramEditSaveInProgress] = useState(false);

    // Fetch programs for the podcast
    const { data: programsData } = useQuery({
        queryKey: ['podcast-programs', program.podcast._id],
        queryFn: () => apiService.getPodcastPrograms(program.podcast._id),
        enabled: isExpanded
    });

    const programs = programsData?.programs || [];

    // Mutation for updating content isPodcast
    const updateIsPodcastMutation = useMutation({
        mutationFn: async ({ contentId, isPodcast }: { contentId: string; isPodcast: boolean }) => {
            return apiService.updateContentIsPodcast(contentId, isPodcast);
        },
        onSuccess: () => {
            // Invalidate the content query to refetch and update toggle state
            queryClient.refetchQueries({ queryKey: ['program-content', program._id, program.podcast._id, selectedWeeks] });
            // Also invalidate programs-analysis to update program metrics (content count, views, etc.)
            queryClient.invalidateQueries({ queryKey: ['programs-analysis-fast'] });
            queryClient.invalidateQueries({ queryKey: ['programs-analysis-prevweek'] });
        }
    });

    // Handler for opening program edit dialog
    const handleEditProgram = (contentId: string, contentTitle: string, currentProgramId: string | undefined) => {
        setProgramEditContentId(contentId);
        setProgramEditContentTitle(contentTitle);
        setProgramEditCurrentProgramId(currentProgramId);
        setProgramEditDialogOpen(true);
    };

    // Handler for saving program assignment
    const handleSaveProgram = async (contentId: string, programId: string | null) => {
        setProgramEditSaveInProgress(true);
        try {
            await apiService.linkProgramToContent(contentId, programId);

            // Refetch the content list for this program
            queryClient.refetchQueries({ queryKey: ['program-content', program._id, program.podcast._id, selectedWeeks] });

            // Refetch the main programs list to update all program rows with new metrics
            // This ensures the program name row shows updated contentCount, totalViews, etc.
            queryClient.refetchQueries({ queryKey: ['programs-analysis-fast'] });
            queryClient.refetchQueries({ queryKey: ['programs-analysis-prevweek'] });

            // Close the dialog
            setProgramEditDialogOpen(false);
            setProgramEditContentId('');
            setProgramEditContentTitle('');
            setProgramEditCurrentProgramId(undefined);
        } catch (error) {
            console.error('Failed to update program:', error);
        } finally {
            setProgramEditSaveInProgress(false);
        }
    };

    return (
        <React.Fragment>
            <TableRow
                hover={isClickable}
                sx={{ cursor: isClickable ? 'pointer' : 'default', '& > *': { borderBottom: 'unset' } }}
                onClick={isClickable ? (e) => onProgramClick(program, e) : undefined}
            >
                <TableCell sx={{ width: '3%' }}>
                    <IconButton
                        size="small"
                        onClick={(e) => onToggleExpand(program._id, e)}
                    >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell align="center" sx={{ width: '3%' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                        {index + 1}
                    </Typography>
                </TableCell>
                <TableCell sx={{ width: '22%' }}>
                    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                        <Avatar
                            src={program.imageS3Key ? getProgramImageUrl(program) : undefined}
                            sx={{ width: 40, height: 40, flexShrink: 0 }}
                        >
                            {!program.imageS3Key && program.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="subtitle2" noWrap title={program.name} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                            {program.name}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell sx={{ width: '11%' }}>
                    <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                        <Avatar
                            src={getPodcastImageUrl(program.podcast)}
                            sx={{ width: 22, height: 22, flexShrink: 0 }}
                        >
                            {program.podcast.title.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography
                            variant="body2"
                            noWrap
                            title={program.podcast.title}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, color: 'text.secondary' }}
                        >
                            {program.podcast.title}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>
                        {program.contentCount}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>
                        {formatNumber(program.totalViews)}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>
                        {formatNumber(Math.round(program.avgViews))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>
                        {formatNumber(Math.round(program.avgViewsDay7 ?? 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>
                        {formatNumber(Math.round(program.totalViewsDay7 ?? 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>
                        {formatNumber(Math.round(program.avgViewsAllTime || 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap sx={{ color: '#2196F3', fontWeight: 500 }}>
                        {formatNumber(Math.round(program.avgInteractions || 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap sx={{ color: '#2196F3', fontWeight: 500 }}>
                        {formatNumber(Math.round(program.avgInteractionsDay7 ?? 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap sx={{ color: '#2196F3', fontWeight: 500 }}>
                        {formatNumber(Math.round(program.totalInteractionsDay7 ?? 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '4.5%' }}>
                    <Typography variant="body2" noWrap sx={{ color: '#9C27B0', fontWeight: 500 }}>
                        {program.uniquePaidBrandsCount}
                    </Typography>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={14}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            {/* 7-Day Average Views Comparison */}
                            {isSingleWeekFilter && (
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        📊 7-Day Average Views Comparison
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                This Week (7-day avg)
                                            </Typography>
                                            <Typography variant="h6" color="primary.main">
                                                {formatNumber(Math.round(program.avgViews))}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Last Week (7-day avg)
                                            </Typography>
                                            <Typography variant="h6" color={program.avgViewsLastWeek !== null && program.avgViewsLastWeek !== undefined ? 'text.primary' : 'text.secondary'}>
                                                {program.avgViewsLastWeek !== null && program.avgViewsLastWeek !== undefined
                                                    ? formatNumber(Math.round(program.avgViewsLastWeek))
                                                    : 'N/A'}
                                            </Typography>
                                        </Box>
                                        {program.avgViewsLastWeek !== null && program.avgViewsLastWeek !== undefined && program.avgViewsLastWeek > 0 && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    % Change
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    color={program.avgViews > program.avgViewsLastWeek ? 'success.main' : program.avgViews < program.avgViewsLastWeek ? 'error.main' : 'text.primary'}
                                                >
                                                    {((program.avgViews - program.avgViewsLastWeek) / program.avgViewsLastWeek * 100).toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}
                            {/* Brands Section */}
                            {brandsLoading ? (
                                <Box display="flex" alignItems="center" gap={1} mb={3}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Loading brands...
                                    </Typography>
                                </Box>
                            ) : brands.length > 0 ? (
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        🏢 Brands in this Program ({program.uniquePaidBrandsCount ?? brands.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
                                        {brands.map((brand) => (
                                            <Chip
                                                key={brand._id}
                                                avatar={
                                                    brand.s3Key ? (
                                                        <Avatar
                                                            src={ensureDirectS3Url(getS3Url(brand.s3Key))}
                                                            alt={brand.name}
                                                        />
                                                    ) : undefined
                                                }
                                                label={`${brand.name} (${brand.mentionCount})`}
                                                color="primary"
                                                variant="outlined"
                                                sx={{ fontSize: '0.875rem' }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            ) : null}

                            {/* Content Section */}
                            {contentLoading ? (
                                <Box display="flex" justifyContent="center" py={2}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : content.length > 0 ? (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Content with Daily Metrics
                                    </Typography>
                                    {content.map((item) => (
                                        <Box key={item._id} sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                                                <Box flex={1}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                                        Published: {item.publishedAt ? (() => {
                                                            const date = new Date(item.publishedAt);
                                                            const day = String(date.getDate()).padStart(2, '0');
                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                            const year = date.getFullYear();
                                                            return `${day}/${month}/${year}`;
                                                        })() : 'N/A'}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    {/* YouTube Link */}
                                                    {item.url && item.source === 'youtube' && (
                                                        <Link
                                                            href={item.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <YouTubeIcon sx={{ color: '#FF0000' }} />
                                                            <Typography variant="caption" color="primary">
                                                                Ver en YouTube
                                                            </Typography>
                                                        </Link>
                                                    )}
                                                    {/* Edit Program Button */}
                                                    <Tooltip title="Edit Program for Episode">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditProgram(item._id, item.title, (item as any).program?._id);
                                                            }}
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {/* isPodcast Toggle - defaults to true if undefined */}
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={item.isPodcast}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        updateIsPodcastMutation.mutate({
                                                                            contentId: item._id,
                                                                            isPodcast: e.target.checked
                                                                        });
                                                                    }}
                                                                    disabled={updateIsPodcastMutation.isPending}
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="caption" color={item.isPodcast !== false ? 'success.main' : 'text.secondary'}>
                                                                    Is Podcast
                                                                </Typography>
                                                            }
                                                            sx={{ mr: 0 }}
                                                        />
                                                        <Tooltip title="Copy content ID">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    void navigator.clipboard.writeText(String(item._id));
                                                                }}
                                                                aria-label="Copy content ID"
                                                                sx={{ color: 'text.secondary' }}
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* Insights Metrics Section */}
                                            {item.insights && (
                                                <Box sx={{ mt: 2, mb: 2 }}>
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <BarChartIcon fontSize="small" color="secondary" />
                                                            <Typography variant="subtitle2" fontWeight="bold" color="secondary">
                                                                Insights Metrics
                                                            </Typography>
                                                        </Box>
                                                        {item.insights.collectedAt && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Updated: {(() => {
                                                                    const date = new Date(item.insights.collectedAt);
                                                                    const day = String(date.getDate()).padStart(2, '0');
                                                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                                                    const year = date.getFullYear();
                                                                    const hours = String(date.getHours()).padStart(2, '0');
                                                                    const minutes = String(date.getMinutes()).padStart(2, '0');
                                                                    return `${day}/${month}/${year} ${hours}:${minutes}`;
                                                                })()}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Grid container spacing={2}>
                                                        {item.insights.views !== undefined && item.insights.views !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Views
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.views)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.likes !== undefined && item.insights.likes !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Likes
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.likes)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.comments !== undefined && item.insights.comments !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Comments
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.comments)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.shares !== undefined && item.insights.shares !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Shares
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.shares)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.saves !== undefined && item.insights.saves !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Saves
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.saves)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.reach !== undefined && item.insights.reach !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Reach
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.reach)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.streams !== undefined && item.insights.streams !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Streams
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.streams)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.uniqueListeners !== undefined && item.insights.uniqueListeners !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Unique Listeners
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.uniqueListeners)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.impressions !== undefined && item.insights.impressions !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Impressions
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.impressions)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                        {item.insights.interactions !== undefined && item.insights.interactions !== null && (
                                                            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Interactions
                                                                    </Typography>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatNumber(item.insights.interactions)}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </Box>
                                            )}

                                            {item.dailyMetrics && item.dailyMetrics.length > 0 ? (
                                                <Box sx={{ mt: 2 }}>
                                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                        <TrendingUpIcon fontSize="small" color="primary" />
                                                        <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                                            Daily Metrics ({item.dailyMetrics.length} days tracked)
                                                        </Typography>
                                                    </Box>
                                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell><strong>Day</strong></TableCell>
                                                                    <TableCell><strong>Date</strong></TableCell>
                                                                    <TableCell align="right"><strong>Views</strong></TableCell>
                                                                    <TableCell align="right"><strong>Likes</strong></TableCell>
                                                                    <TableCell align="right"><strong>Comments</strong></TableCell>
                                                                    <TableCell align="right"><strong>Δ Views</strong></TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {(() => {
                                                                    const sortedMetrics = item.dailyMetrics!.sort((a, b) => new Date(a.collectedAt).getTime() - new Date(b.collectedAt).getTime());

                                                                    // Use the selectedMetricDate from backend to identify which metric to highlight
                                                                    const selectedDate = item.selectedMetricDate;

                                                                    return sortedMetrics.map((metric, idx, arr) => {
                                                                        const prevMetric = idx > 0 ? arr[idx - 1] : null;
                                                                        const viewsDelta = prevMetric ? metric.views - prevMetric.views : metric.views;

                                                                        const collectedDate = new Date(metric.collectedAt);
                                                                        const daysSincePublished = getDisplayDaySincePublished(
                                                                            item.publishedAt,
                                                                            metric.collectedAt,
                                                                            metric.daysSincePublished,
                                                                        );

                                                                        const chileanDate = new Date(collectedDate.getTime() - (3 * 60 * 60 * 1000));
                                                                        // Format as DD/MM/YYYY
                                                                        const day = String(chileanDate.getUTCDate()).padStart(2, '0');
                                                                        const month = String(chileanDate.getUTCMonth() + 1).padStart(2, '0');
                                                                        const year = chileanDate.getUTCFullYear();
                                                                        const chileanDateStr = `${day}/${month}/${year}`;

                                                                        // Check if this metric is the one used for ranking
                                                                        const isSelectedMetric = selectedDate && metric.collectedAt === selectedDate;

                                                                        return (
                                                                            <TableRow
                                                                                key={idx}
                                                                                sx={{
                                                                                    '&:hover': { bgcolor: 'action.hover' },
                                                                                    bgcolor: isSelectedMetric ? 'success.50' : 'inherit'
                                                                                }}
                                                                            >
                                                                                <TableCell>
                                                                                    <Chip
                                                                                        label={`Day ${daysSincePublished}`}
                                                                                        size="small"
                                                                                        color={isSelectedMetric ? 'success' : 'default'}
                                                                                        sx={{ fontWeight: 'bold', minWidth: 60 }}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Typography variant="body2">
                                                                                        {chileanDateStr}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell align="right">
                                                                                    <Typography variant="body2" fontWeight="medium">
                                                                                        {metric.views.toLocaleString()}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell align="right">
                                                                                    <Typography variant="body2">
                                                                                        {metric.likes.toLocaleString()}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell align="right">
                                                                                    <Typography variant="body2">
                                                                                        {metric.comments.toLocaleString()}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                                <TableCell align="right">
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        color={viewsDelta > 0 ? 'success.main' : 'text.secondary'}
                                                                                        fontWeight={viewsDelta > 0 ? 'bold' : 'normal'}
                                                                                    >
                                                                                        {viewsDelta > 0 ? '+' : ''}{viewsDelta.toLocaleString()}
                                                                                    </Typography>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    });
                                                                })()}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    No daily metrics available
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No content found for this program
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            {/* Program Edit Dialog */}
            <ProgramEditDialog
                open={programEditDialogOpen}
                onClose={() => {
                    setProgramEditDialogOpen(false);
                    setProgramEditContentId('');
                    setProgramEditContentTitle('');
                    setProgramEditCurrentProgramId(undefined);
                }}
                contentId={programEditContentId}
                contentTitle={programEditContentTitle}
                currentProgramId={programEditCurrentProgramId}
                programs={programs}
                onSave={handleSaveProgram}
                saveInProgress={programEditSaveInProgress}
            />
        </React.Fragment>
    );
};

const ChannelRow: React.FC<{
    channel: ChannelAnalysisData;
    index: number;
    isExpanded: boolean;
    onToggleExpand: (channelId: string, event: React.MouseEvent) => void;
    formatNumber: (num: number) => string;
    selectedWeeks?: string[];
    isSingleWeekFilter?: boolean;
}> = ({ channel, index, isExpanded, onToggleExpand, formatNumber, selectedWeeks = ['all'], isSingleWeekFilter = false }) => {
    const navigate = useNavigate();

    const weekParam = selectedWeeks.includes('all') || selectedWeeks.length === 0
        ? undefined
        : selectedWeeks.join(',');

    return (
        <React.Fragment>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell sx={{ width: '3%' }}>
                    <IconButton
                        size="small"
                        onClick={(e) => onToggleExpand(channel._id, e)}
                    >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell align="center" sx={{ width: '3%' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                        {index + 1}
                    </Typography>
                </TableCell>
                <TableCell sx={{ width: '22%' }}>
                    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                        <Avatar
                            src={getPodcastImageUrl(channel.podcast)}
                            sx={{ width: 40, height: 40, flexShrink: 0 }}
                        >
                            {channel.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="subtitle2" noWrap title={channel.name} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                            {channel.name}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell align="center" sx={{ width: '11%' }}>
                    <Chip
                        label={channel.programCount}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>{channel.contentCount}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>{formatNumber(channel.totalViews)}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>{formatNumber(Math.round(channel.avgViews))}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>{formatNumber(Math.round(channel.avgViewsDay7 ?? 0))}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>{formatNumber(Math.round(channel.totalViewsDay7 ?? 0))}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap>{formatNumber(Math.round(channel.avgViewsAllTime || 0))}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap sx={{ color: '#2196F3', fontWeight: 500 }}>
                        {formatNumber(Math.round(channel.avgInteractions || 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap sx={{ color: '#2196F3', fontWeight: 500 }}>
                        {formatNumber(Math.round(channel.avgInteractionsDay7 ?? 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '5%' }}>
                    <Typography variant="body2" noWrap sx={{ color: '#2196F3', fontWeight: 500 }}>
                        {formatNumber(Math.round(channel.totalInteractionsDay7 ?? 0))}
                    </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '4.5%' }}>
                    <Typography variant="body2" noWrap sx={{ color: '#9C27B0', fontWeight: 500 }}>
                        {channel.uniquePaidBrandsCount}
                    </Typography>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={14}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            {isSingleWeekFilter && channel.avgViewsLastWeek != null && (
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        📊 7-Day Average Views Comparison
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                This Week (7-day avg)
                                            </Typography>
                                            <Typography variant="h6" color="primary.main">
                                                {formatNumber(Math.round(channel.avgViews))}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Last Week (7-day avg)
                                            </Typography>
                                            <Typography variant="h6" color="text.primary">
                                                {formatNumber(Math.round(channel.avgViewsLastWeek))}
                                            </Typography>
                                        </Box>
                                        {channel.avgViewsLastWeek > 0 && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    % Change
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    color={channel.avgViews > channel.avgViewsLastWeek ? 'success.main' : channel.avgViews < channel.avgViewsLastWeek ? 'error.main' : 'text.primary'}
                                                >
                                                    {(((channel.avgViews - channel.avgViewsLastWeek) / channel.avgViewsLastWeek) * 100).toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h6" gutterBottom>
                                    Programs in this channel ({channel.programCount})
                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Program</TableCell>
                                                <TableCell align="right">Content</TableCell>
                                                <TableCell align="right">Total Views</TableCell>
                                                <TableCell align="right">Avg Views</TableCell>
                                                <TableCell align="right">Paid Brands</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {channel.programs.map((program) => (
                                                <TableRow key={program._id}>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Avatar
                                                                src={program.imageS3Key ? getProgramImageUrl(program) : undefined}
                                                                sx={{ width: 28, height: 28 }}
                                                            >
                                                                {!program.imageS3Key && program.name.charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <Typography variant="body2">{program.name}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right">{program.contentCount}</TableCell>
                                                    <TableCell align="right">{formatNumber(program.totalViews)}</TableCell>
                                                    <TableCell align="right">{formatNumber(Math.round(program.avgViews))}</TableCell>
                                                    <TableCell align="right">{program.uniquePaidBrandsCount}</TableCell>
                                                    <TableCell align="center">
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() => {
                                                                const { startDate, endDate } = getDefaultProgramMetricsDateRange();
                                                                const params = new URLSearchParams({ startDate, endDate });
                                                                navigate(`/programs/${program._id}?${params.toString()}`);
                                                            }}
                                                        >
                                                            Ver programa
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                            <ChannelBrandsSection
                                podcastId={channel._id}
                                week={weekParam}
                                podcastTitle={channel.name}
                                formatNumber={formatNumber}
                            />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const ProgramAnalysisReview: React.FC = () => {
    const navigate = useNavigate();

    // Generate week options (last 12 weeks) in Chilean timezone
    const weekOptions = useMemo(() => {
        const weeks = [{ value: 'all', label: 'All Weeks' }];
        const nowChilean = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' }));

        for (let i = 0; i < 12; i++) {
            const weekStart = new Date(nowChilean);
            const daysSinceMonday = (nowChilean.getDay() + 6) % 7; // 0 = Monday, 1 = Tuesday, etc.
            weekStart.setDate(nowChilean.getDate() - (daysSinceMonday + i * 7));
            weekStart.setHours(0, 0, 0, 0);

            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6); // Monday + 6 days = Sunday
            weekEnd.setHours(23, 59, 59, 999);

            const formatDate = (date: Date) => {
                const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'America/Santiago' });
                const day = date.getDate();
                return `${month} ${day}`;
            };

            // Format date as YYYY-MM-DD in Chilean timezone without UTC conversion
            const year = weekStart.getFullYear();
            const month = String(weekStart.getMonth() + 1).padStart(2, '0');
            const day = String(weekStart.getDate()).padStart(2, '0');
            const dateValue = `${year}-${month}-${day}`;

            weeks.push({
                value: dateValue,
                label: `${formatDate(weekStart)} - ${formatDate(weekEnd)}, ${year}`
            });
        }

        return weeks;
    }, []);

    // Last week's value (Monday YYYY-MM-DD) for default when mode is week
    const lastWeekValue = useMemo(() => {
        const nowChilean = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' }));
        const weekStart = new Date(nowChilean);
        const daysSinceMonday = (nowChilean.getDay() + 6) % 7;
        weekStart.setDate(nowChilean.getDate() - (daysSinceMonday + 7)); // 1 week ago
        weekStart.setHours(0, 0, 0, 0);
        const year = weekStart.getFullYear();
        const month = String(weekStart.getMonth() + 1).padStart(2, '0');
        const day = String(weekStart.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    // Generate month options (last 12 months) in Chilean timezone
    const monthOptions = useMemo(() => {
        const months = [{ value: 'all', label: 'All Months' }];
        const nowChilean = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' }));

        for (let i = 0; i < 12; i++) {
            const monthDate = new Date(nowChilean);
            monthDate.setMonth(nowChilean.getMonth() - i);

            // First day of month at 00:00:00 Chilean time
            const firstDay = new Date(monthDate);
            firstDay.setDate(1);
            firstDay.setHours(0, 0, 0, 0);

            // Last day of month at 23:59:59 Chilean time
            const lastDay = new Date(monthDate);
            lastDay.setMonth(monthDate.getMonth() + 1);
            lastDay.setDate(0); // Sets to last day of previous month
            lastDay.setHours(23, 59, 59, 999);

            const year = firstDay.getFullYear();
            const month = String(firstDay.getMonth() + 1).padStart(2, '0');
            const monthName = firstDay.toLocaleDateString('en-US', { month: 'long', timeZone: 'America/Santiago' });

            // Format as YYYY-MM-01,YYYY-MM-LastDay to represent full month range
            const firstDayStr = `${year}-${month}-01`;
            const lastDayNum = lastDay.getDate();
            const lastDayStr = `${year}-${month}-${String(lastDayNum).padStart(2, '0')}`;
            const dateValue = `${firstDayStr},${lastDayStr}`;

            months.push({
                value: dateValue,
                label: `${monthName} ${year}`
            });
        }

        return months;
    }, []);

    // Generate year options (2025, 2026, and All Years)
    const yearOptions = useMemo(() => {
        const years = [{ value: 'all', label: 'All Years' }];

        // Add 2025 and 2026
        [2025, 2026].forEach(year => {
            // First day of year at 00:00:00 Chilean time
            const firstDayStr = `${year}-01-01`;
            // Last day of year at 23:59:59 Chilean time
            const lastDayStr = `${year}-12-31`;
            const dateValue = `${firstDayStr},${lastDayStr}`;

            years.push({
                value: dateValue,
                label: `${year}`
            });
        });

        return years;
    }, []);

    // State
    const [activeTab, setActiveTab] = useState<number>(0);
    const [dateFilterMode, setDateFilterMode] = useState<'week' | 'month' | 'year'>('week');
    const [selectedWeeks, setSelectedWeeks] = useState<string[]>(() => [lastWeekValue]);
    const podcastTaxonomy = PROGRAM_ANALYSIS_ROUTE_TAXONOMY;
    const [selectedPodcastId, setSelectedPodcastId] = useState<string>('all');
    const [selectedProgramId, setSelectedProgramId] = useState<string>('all');
    const [minContentCount, setMinContentCount] = useState<number | undefined>(undefined);
    const [orderBy, setOrderBy] = useState<string>('avgViews');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());
    const [selectedRankingParam, setSelectedRankingParam] = useState<RankingParam>('views');
    const [weeklyIgCopyDrafts, setWeeklyIgCopyDrafts] = useState<Partial<Record<RankingParam, string>>>(
        () => loadWeeklyIgCopyDrafts(),
    );
    const [weeklyIgCopyEditing, setWeeklyIgCopyEditing] = useState<RankingParam | null>(null);
    const [weeklyIgDownloading, setWeeklyIgDownloading] = useState<RankingParam | null>(null);
    const [weeklyIgEmailTo, setWeeklyIgEmailTo] = useState('esteban@podscope.co');
    const [weeklyIgEmailSending, setWeeklyIgEmailSending] = useState(false);
    const weeklyIgDownloadFrameRef = React.useRef<HTMLIFrameElement | null>(null);
    React.useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (
                event.data?.type !== 'program-asset-png-downloaded' &&
                event.data?.type !== 'program-asset-png-download-failed'
            ) {
                return;
            }
            weeklyIgDownloadFrameRef.current?.remove();
            weeklyIgDownloadFrameRef.current = null;
            setWeeklyIgDownloading(null);
            setSnackbarMessage(
                event.data.type === 'program-asset-png-downloaded'
                    ? 'Asset descargado'
                    : 'No se pudo descargar el asset',
            );
        };
        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('message', onMessage);
            weeklyIgDownloadFrameRef.current?.remove();
        };
    }, []);
    React.useEffect(() => {
        localStorage.setItem(WEEKLY_IG_COPY_STORAGE_KEY, JSON.stringify(weeklyIgCopyDrafts));
    }, [weeklyIgCopyDrafts]);
    /** Explorer livestream API category: all channel types vs digital-only (ranking assets + social copy). */
    const [livestreamExplorerCategory, setLivestreamExplorerCategory] = useState<'all' | 'digital'>('digital');

    const [mainTableEntity, setMainTableEntity] = useState<'programs' | 'channels'>('programs');
    const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());

    // Guest table sorting state
    const [guestOrderBy, setGuestOrderBy] = useState<string>('viewsImpact');
    const [guestOrder, setGuestOrder] = useState<'asc' | 'desc'>('desc');
    const [expandedGuests, setExpandedGuests] = useState<Set<string>>(new Set());
    const [excludeHosts, setExcludeHosts] = useState<boolean>(false);

    // Guest action menu state
    const [guestMenuAnchor, setGuestMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedGuestForAction, setSelectedGuestForAction] = useState<{ _id: string; guestName: string } | null>(null);
    const [selectedProgramForHost, setSelectedProgramForHost] = useState<{ _id: string; name: string } | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

    const queryClient = useQueryClient();

    // Brands table sorting state
    const [brandOrderBy, setBrandOrderBy] = useState<string>('programCount');
    const [brandOrder, setBrandOrder] = useState<'asc' | 'desc'>('desc');
    const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set()); // Stores brandId
    const [expandedBrandChannels, setExpandedBrandChannels] = useState<Set<string>>(new Set()); // Stores brandId-podcastId for brand channels
    const [selectedBrandCategory, setSelectedBrandCategory] = useState<string>('all');
    const [brandSearchQuery, setBrandSearchQuery] = useState<string>('');

    // Category rankings table sorting state
    const [categoryRankingsOrderBy, setCategoryRankingsOrderBy] = useState<string>('brandCount');
    const [categoryRankingsOrder, setCategoryRankingsOrder] = useState<'asc' | 'desc'>('desc');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set()); // Stores category names
    const [expandedCategoryBrands, setExpandedCategoryBrands] = useState<Set<string>>(new Set()); // `${category}::${brandId}`

    // Brand category change state
    const [categoryChangeAnchor, setCategoryChangeAnchor] = useState<{ element: HTMLElement; brandId: string; brandName: string } | null>(null);
    const [selectedNewCategory, setSelectedNewCategory] = useState<string>('');

    // Brand merge state (Brands sub-tab)
    const [brandMergeMenuAnchor, setBrandMergeMenuAnchor] = useState<{ element: HTMLElement; brand: BrandMergeSource & { category?: string; brandCategory?: string } } | null>(null);
    const [mergeDialogBrand, setMergeDialogBrand] = useState<BrandMergeSource | null>(null);

    // Brands sub-tab state (categories vs brands)
    const [brandsSubTab, setBrandsSubTab] = useState<number>(0); // 0 = Categories, 1 = Brands

    // Top Content table sorting state
    const [topContentOrderBy, setTopContentOrderBy] = useState<string>('views');
    const [topContentOrder, setTopContentOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedTopContentId, setSelectedTopContentId] = useState<string | null>(null);
    const [topContentPage, setTopContentPage] = useState<number>(1);
    const [topContentSearch, setTopContentSearch] = useState<string>('');

    // Time Series state
    const [timeSeriesGranularity, setTimeSeriesGranularity] = useState<'daily' | 'monthly'>('daily');
    const [excludeSingleProgramBrands, setExcludeSingleProgramBrands] = useState<boolean>(false);
    const [consistentProgramsOnly, setConsistentProgramsOnly] = useState<boolean>(false);
    const [visibleMetrics, setVisibleMetrics] = useState<Set<string>>(new Set([
        'Content Count',
        'Total Views',
        'Total Interactions',
        'Paid Brands',
        'Engagement Rate'
    ]));

    // Delete mention mutation — same hard-delete path as podcast review
    // (AI mentions require ?hard=true; without it the API returns 400)
    const deleteMentionMutation = useMutation({
        mutationFn: (mentionId: string) => apiService.deleteBrandMention(mentionId, true),
        onSuccess: (_data, mentionId) => {
            queryClient.setQueriesData<{ mentions?: Array<{ _id: string }> }>(
                { queryKey: ['brand-analytics-channel-mentions'] },
                (old) => {
                    if (!old?.mentions) return old;
                    return {
                        ...old,
                        mentions: old.mentions.filter((mention) => mention._id !== mentionId),
                    };
                },
            );
            queryClient.invalidateQueries({ queryKey: ['brands-analytics-by-programs'] });
            queryClient.invalidateQueries({ queryKey: ['brand-category-rankings'] });
            queryClient.invalidateQueries({ queryKey: ['brand-analytics-channel-mentions'] });
            setSnackbarMessage('Mention deleted');
        },
        onError: (error: any) => {
            setSnackbarMessage(
                error?.response?.data?.error || 'Failed to delete mention',
            );
        },
    });

    const handleDeleteMention = (mentionId: string, _brandId: string, _podcastId: string) => {
        deleteMentionMutation.mutate(mentionId);
    };

    // Helper function to build date parameters based on filter mode
    const buildDateParams = () => {
        const hasAll = selectedWeeks.includes('all');
        if (hasAll || selectedWeeks.length === 0) {
            return { week: undefined, year: undefined, startDate: undefined, endDate: undefined };
        }

        if (dateFilterMode === 'year') {
            // For year mode, extract start and end dates from selected years
            const yearRanges = selectedWeeks.map(yearRange => {
                const [startDate, endDate] = yearRange.split(',');
                return { startDate, endDate };
            });

            // Find earliest start and latest end
            const earliestStart = yearRanges.reduce((min, range) =>
                range.startDate < min ? range.startDate : min, yearRanges[0]!.startDate);
            const latestEnd = yearRanges.reduce((max, range) =>
                range.endDate > max ? range.endDate : max, yearRanges[0]!.endDate);

            return {
                week: undefined,
                year: selectedWeeks.join(','),
                startDate: earliestStart,
                endDate: latestEnd
            };
        } else if (dateFilterMode === 'month') {
            // For month mode, extract start and end dates from selected months
            // Month format: "YYYY-MM-01,YYYY-MM-LastDay"
            const monthRanges = selectedWeeks.map(monthRange => {
                const [startDate, endDate] = monthRange.split(',');
                return { startDate, endDate };
            });

            // Find earliest start and latest end
            const earliestStart = monthRanges.reduce((min, range) =>
                range.startDate! < min ? range.startDate! : min, monthRanges[0]!.startDate!);
            const latestEnd = monthRanges.reduce((max, range) =>
                range.endDate! > max ? range.endDate! : max, monthRanges[0]!.endDate!);

            return {
                week: undefined,
                year: undefined,
                startDate: earliestStart,
                endDate: latestEnd
            };
        } else {
            // For week mode, use week parameter
            return {
                week: selectedWeeks.join(','),
                year: undefined,
                startDate: undefined,
                endDate: undefined
            };
        }
    };

    // Same date window as programs analysis for GET /programs/:id/brands (primitives only for stable query keys)
    const _programBrandsDates = buildDateParams();
    const programBrandsDateQuery = {
        week: _programBrandsDates.week,
        startDate: _programBrandsDates.startDate,
        endDate: _programBrandsDates.endDate,
    };

    // Fetch programs data (fast - skips prev week comparison)
    const {
        data: programsDataFast,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['programs-analysis-fast', { selectedWeeks, dateFilterMode, PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, selectedPodcastId, orderBy: mainTableEntity === 'programs' ? orderBy : 'avgViews', order: mainTableEntity === 'programs' ? order : 'desc', minContentCount, mainTableEntity }],
        queryFn: () => {
            const dateParams = buildDateParams();

            return apiService.getProgramsAnalysis(
                dateParams.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                mainTableEntity === 'programs' ? orderBy : 'avgViews',
                mainTableEntity === 'programs' ? order : 'desc',
                dateParams.startDate,
                dateParams.endDate,
                true, // skipPrevWeek for fast initial load
                minContentCount
            );
        },
        staleTime: 0,
    });

    // Check if we need prev week data (single week selected and in week mode)
    const isSingleWeekSelected = dateFilterMode === 'week' && selectedWeeks.length === 1 && selectedWeeks[0] !== 'all';

    // Fetch prev week data separately (only for single week filter)
    const { data: prevWeekData } = useQuery({
        queryKey: ['programs-analysis-prevweek', { selectedWeeks, dateFilterMode, PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, selectedPodcastId, orderBy: mainTableEntity === 'programs' ? orderBy : 'avgViews', order: mainTableEntity === 'programs' ? order : 'desc', minContentCount, mainTableEntity }],
        queryFn: () => {
            const dateParams = buildDateParams();

            return apiService.getProgramsAnalysis(
                dateParams.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                mainTableEntity === 'programs' ? orderBy : 'avgViews',
                mainTableEntity === 'programs' ? order : 'desc',
                dateParams.startDate,
                dateParams.endDate,
                false, // include prev week data
                minContentCount
            );
        },
        enabled: isSingleWeekSelected && !!programsDataFast, // Only run after fast data loaded
        staleTime: 0, // Cache for 1 minute
    });

    // Merge fast data with prev week data when available
    const programsData = useMemo(() => {
        if (!programsDataFast) return undefined;

        // If we have prev week data, merge it
        if (prevWeekData && isSingleWeekSelected) {
            return prevWeekData;
        }

        return programsDataFast;
    }, [programsDataFast, prevWeekData, isSingleWeekSelected]);

    // Fetch podcasts for filter dropdown
    const { data: podcastsData } = useQuery({
        queryKey: ['podcasts-list-for-filter'],
        queryFn: async () => {
            const response = await apiService.getPodcasts(1, 1000);
            return response;
        },
    });

    // Fetch channels/brands for social copy when that ranking type is selected
    const dateParamsForRanking = useMemo(
        () => buildDateParams(),
        // buildDateParams is stable in practice (uses selectedWeeks, dateFilterMode)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedWeeks, dateFilterMode]
    );
    const livestreamRankingDateRange = useMemo(
        () => getLivestreamRankingDateRange(dateFilterMode, selectedWeeks, dateParamsForRanking),
        [dateFilterMode, selectedWeeks, dateParamsForRanking],
    );
    const { data: rankingChannelsData } = useQuery({
        queryKey: ['ranking-channels-social', dateParamsForRanking, PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, selectedPodcastId],
        queryFn: () =>
            apiService.getChannelsSocialAnalytics(
                dateParamsForRanking.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                dateParamsForRanking.year,
                dateParamsForRanking.startDate,
                dateParamsForRanking.endDate
            ),
        enabled: activeTab === 0 && selectedRankingParam === 'channels-social',
    });
    const livestreamExplorerRankingMode =
        selectedRankingParam === 'livestream-programs-program' ? 'programs' : 'channels';

    const { data: rankingLivestreamData } = useQuery({
        queryKey: [
            'ranking-livestream-programs',
            livestreamExplorerCategory,
            livestreamExplorerRankingMode,
            livestreamRankingDateRange,
        ],
        queryFn: () =>
            apiService.getExplorerLivestreams(
                livestreamRankingDateRange!.startDate,
                livestreamRankingDateRange!.endDate,
                'all',
                livestreamExplorerCategory,
                livestreamExplorerRankingMode === 'programs'
                    ? {
                          ranking: {
                              page: 0,
                              pageSize: 30,
                              mode: 'programs',
                              sortColumn: 'avgViewers',
                              sortDirection: 'desc',
                          },
                      }
                    : undefined,
            ),
        enabled:
            activeTab === 0 &&
            isExplorerLivestreamRankingParam(selectedRankingParam) &&
            livestreamRankingDateRange != null,
    });

    const { data: weeklyIgLivestreamData } = useQuery({
        queryKey: ['weekly-ig-livestream-programs', livestreamRankingDateRange],
        queryFn: () =>
            apiService.getExplorerLivestreams(
                livestreamRankingDateRange!.startDate,
                livestreamRankingDateRange!.endDate,
                'all',
                'digital',
                {
                    ranking: {
                        page: 0,
                        pageSize: 1,
                        mode: 'programs',
                        sortColumn: 'avgViewers',
                        sortDirection: 'desc',
                    },
                },
            ),
        enabled: activeTab === 0 && livestreamRankingDateRange != null,
    });

    const { data: rankingWorldCupViewsData } = useQuery({
        queryKey: [
            'ranking-world-cup-views',
            dateParamsForRanking,
            PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE,
            selectedPodcastId,
            minContentCount,
        ],
        queryFn: () => {
            const dateParams = dateParamsForRanking;
            return apiService.getProgramsAnalysis(
                dateParams.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                'avgViewsDay7',
                'desc',
                dateParams.startDate,
                dateParams.endDate,
                false,
                minContentCount,
                true,
            );
        },
        enabled: activeTab === 0 && selectedRankingParam === 'world-cup-views',
    });

    const { data: rankingWorldCupLivestreamData } = useQuery({
        queryKey: ['ranking-world-cup-livestream-programs', livestreamRankingDateRange],
        queryFn: () =>
            apiService.getExplorerLivestreams(
                livestreamRankingDateRange!.startDate,
                livestreamRankingDateRange!.endDate,
                'all',
                'all',
                {
                    worldCupOnly: true,
                    ranking: {
                        page: 0,
                        pageSize: 30,
                        mode: 'programs',
                        sortColumn: 'avgViewers',
                        sortDirection: 'desc',
                    },
                },
            ),
        enabled:
            activeTab === 0 &&
            selectedRankingParam === 'world-cup-livestream-programs' &&
            livestreamRankingDateRange != null,
    });

    // Fetch guest analytics
    const {
        data: guestsData,
        isLoading: guestsLoading,
        error: guestsError,
    } = useQuery({
        queryKey: ['guests-analytics', { selectedWeeks, dateFilterMode, PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, selectedPodcastId, minContentCount }],
        queryFn: () => {
            const dateParams = buildDateParams();
            return apiService.getGuestAnalytics(
                dateParams.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                dateParams.year,
                dateParams.startDate,
                dateParams.endDate,
                minContentCount
            );
        },
        staleTime: 0,
        enabled: activeTab === 1, // Only fetch when guest tab is active
    });

    // Fetch brands analytics by programs
    const {
        data: brandsData,
        isLoading: brandsLoading,
        error: brandsError,
    } = useQuery({
        queryKey: ['brands-analytics-by-programs', { selectedWeeks, dateFilterMode, PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, selectedPodcastId, minContentCount }],
        queryFn: () => {
            const dateParams = buildDateParams();
            return apiService.getBrandsAnalyticsByPrograms(
                dateParams.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                dateParams.year,
                dateParams.startDate,
                dateParams.endDate,
                minContentCount
            );
        },
        staleTime: 0,
        enabled: activeTab === 2, // Brands tab: needed for Brands sub-tab and category rankings mention drill-down
    });

    // Fetch brand categories
    const { data: brandCategoriesData } = useQuery({
        queryKey: ['brand-categories'],
        queryFn: () => apiService.getBrandCategories(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        enabled: activeTab === 2, // Fetch when brands tab is active (needed for both sub-tabs)
    });

    // Fetch top content by views (100 per page, up to 10 pages; optional title search)
    const {
        data: topContentData,
        isLoading: topContentLoading,
        error: topContentError,
    } = useQuery({
        queryKey: ['top-content-analytics', { selectedWeeks, dateFilterMode, PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, selectedPodcastId, minContentCount, topContentPage, topContentSearch }],
        queryFn: () => {
            const dateParams = buildDateParams();
            return apiService.getTopContentAnalytics(
                dateParams.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                100,
                dateParams.year,
                dateParams.startDate,
                dateParams.endDate,
                topContentPage,
                topContentSearch.trim() || undefined
            );
        },
        staleTime: 0,
        enabled: activeTab === 3,
    });

    const topContentTotal = topContentData?.total ?? 0;
    const topContentTotalPages = Math.min(10, Math.ceil(topContentTotal / 100) || 1);

    React.useEffect(() => {
        if (topContentTotalPages >= 1 && topContentPage > topContentTotalPages) {
            setTopContentPage(1);
        }
    }, [topContentTotalPages, topContentPage]);

    // Fetch selected content's daily metrics for growth chart (Top Content tab)
    const { data: selectedContentMetrics, isLoading: selectedContentMetricsLoading, isError: selectedContentMetricsError } = useQuery({
        queryKey: ['content-daily-metrics', selectedTopContentId],
        queryFn: () => apiService.getContentDailyMetrics(selectedTopContentId!),
        enabled: activeTab === 3 && !!selectedTopContentId,
    });

    // Fetch time-series analytics
    const {
        data: timeSeriesData,
        isLoading: timeSeriesLoading,
        error: timeSeriesError,
    } = useQuery({
        queryKey: ['time-series-analytics', { selectedWeeks, dateFilterMode, PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, selectedPodcastId, selectedProgramId, timeSeriesGranularity, minContentCount, excludeSingleProgramBrands, consistentProgramsOnly }],
        queryFn: () => {
            const dateParams = buildDateParams();
            return apiService.getTimeSeriesAnalytics(
                dateParams.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                selectedProgramId !== 'all' ? selectedProgramId : undefined,
                dateParams.year,
                dateParams.startDate,
                dateParams.endDate,
                timeSeriesGranularity,
                minContentCount,
                excludeSingleProgramBrands,
                consistentProgramsOnly
            );
        },
        staleTime: 0,
        enabled: activeTab === 4, // Only fetch when time series tab is active
    });

    const programs = useMemo(() => {
        return (programsData?.programs as ProgramAnalysisData[]) || [];
    }, [programsData?.programs]);
    const podcasts = useMemo(
        () => (podcastsData?.podcasts || []).filter(matchesProgramAnalysisRouteTaxonomy),
        [podcastsData?.podcasts],
    );

    // Ranking period label for social copy (e.g. "Semana del 16 al 22 de Febrero")
    const rankingPeriodLabel = useMemo(() => {
        const p = dateParamsForRanking;
        if (p.week) {
            const weekStr = p.week.split(',')[0];
            if (weekStr) {
                const start = new Date(weekStr + 'T12:00:00');
                if (!isNaN(start.getTime())) {
                    const end = new Date(start);
                    end.setDate(end.getDate() + 6);
                    const d1 = start.getDate();
                    const d2 = end.getDate();
                    const month1 = RANKING_MONTHS_ES[start.getMonth()];
                    const month2 = RANKING_MONTHS_ES[end.getMonth()];
                    if (month1 === month2) return `Semana del ${d1} al ${d2} de ${month1}`;
                    return `Semana del ${d1} de ${month1} al ${d2} de ${month2}`;
                }
            }
        }
        if (p.startDate && p.endDate) return `${p.startDate} – ${p.endDate}`;
        return 'Período seleccionado';
    }, [dateParamsForRanking]);

    const weeklyIgDateRange = useMemo(
        () => formatWeeklyIgDateRange(dateParamsForRanking),
        [dateParamsForRanking],
    );
    const weeklyIgProgramCount = programsData?.activeProgramCount ?? programs.length;
    const weeklyIgChannelCount = useMemo(
        () => countChannelsFromPrograms(programs as Parameters<typeof countChannelsFromPrograms>[0]),
        [programs],
    );
    const weeklyIgLiveProgramCount = weeklyIgLivestreamData?.ranking?.total;

    const weeklyIgFilledCopy = (item: (typeof WEEKLY_IG_RANKINGS)[number]) => {
        const template = weeklyIgCopyDrafts[item.param] ?? item.copy;
        const count =
            item.param === 'livestream-programs-program'
                ? weeklyIgLiveProgramCount
                : item.countPlaceholder === '{channelCount}'
                  ? weeklyIgChannelCount
                  : weeklyIgProgramCount;
        const countLabel = count == null ? '…' : count.toLocaleString('es-CL');
        return fillWeeklyIgCopy(template, {
            dateRange: weeklyIgDateRange,
            programCount: item.countPlaceholder === '{programCount}' ? countLabel : weeklyIgProgramCount.toLocaleString('es-CL'),
            channelCount: item.countPlaceholder === '{channelCount}' ? countLabel : weeklyIgChannelCount.toLocaleString('es-CL'),
        });
    };

    // Build ranking lines for social copy (for the selected ranking type; with @handles for preview/IG if needed)
    const socialPostRankingLines = useMemo(() => {
        const type = RANKING_TYPES.find((t) => t.param === selectedRankingParam);
        if (!type) return '';
        if (type.param === 'views') {
            const items: SocialPostRankingItem[] = [...programs]
                .sort((a, b) => b.avgViews - a.avgViews)
                .slice(0, 10)
                .map((p, i) => ({ position: i + 1, programName: p.name, instagramHandle: p.podcast.socialAccounts?.instagram ?? '' }));
            return buildSocialPostRankingLines(items);
        }
        if (type.param === 'channels-views') {
            return buildChannelsViewsRankingRows(programs)
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.name}`)
                .join('\n');
        }
        if (type.param === 'channels-interactions') {
            return buildChannelsInteractionsRankingRows(programs)
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.name}`)
                .join('\n');
        }
        if (type.param === 'interactions') {
            const items: SocialPostRankingItem[] = [...programs]
                .sort((a, b) => (b.avgInteractions ?? 0) - (a.avgInteractions ?? 0))
                .slice(0, 10)
                .map((p, i) => ({ position: i + 1, programName: p.name, instagramHandle: p.podcast.socialAccounts?.instagram ?? '' }));
            return buildSocialPostRankingLines(items);
        }
        if (type.param === 'interactions-total') {
            const items: SocialPostRankingItem[] = [...programs]
                .sort((a, b) => (b.totalInteractionsDay7 ?? 0) - (a.totalInteractionsDay7 ?? 0))
                .slice(0, 10)
                .map((p, i) => ({ position: i + 1, programName: p.name, instagramHandle: p.podcast.socialAccounts?.instagram ?? '' }));
            return buildSocialPostRankingLines(items);
        }
        if (type.param === 'programs-brands') {
            const items: SocialPostRankingItem[] = [...programs]
                .sort((a, b) => (b.uniquePaidBrandsCount ?? 0) - (a.uniquePaidBrandsCount ?? 0))
                .slice(0, 10)
                .map((p, i) => ({ position: i + 1, programName: p.name, instagramHandle: p.podcast.socialAccounts?.instagram ?? '' }));
            return buildSocialPostRankingLines(items);
        }
        if (type.param === 'channels-social' && rankingChannelsData?.channels) {
            return rankingChannelsData.channels
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.name}`)
                .join('\n');
        }
        if (type.param === 'livestream-programs' && rankingLivestreamData?.channels) {
            const sorted = [...rankingLivestreamData.channels]
                .filter((c) => !isExcludedLivestreamRankingChannelName(c.channelName))
                .sort(
                    (a, b) => (b.metrics?.avgViewers ?? 0) - (a.metrics?.avgViewers ?? 0),
                );
            return sorted
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.channelName}`)
                .join('\n');
        }
        if (type.param === 'livestream-programs-program' && rankingLivestreamData?.ranking?.programRows) {
            const sorted = [...rankingLivestreamData.ranking.programRows]
                .filter((row) =>
                    !isExcludedLivestreamRankingChannelName(row.sampleChannelName),
                )
                .sort(
                    (a, b) => (b.metrics?.avgViewers ?? 0) - (a.metrics?.avgViewers ?? 0),
                );
            return sorted
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.programName}`)
                .join('\n');
        }
        if (type.param === 'world-cup-views' && rankingWorldCupViewsData?.programs) {
            const items: SocialPostRankingItem[] = [...rankingWorldCupViewsData.programs]
                .sort((a, b) => (b.avgViewsDay7 ?? b.avgViews ?? 0) - (a.avgViewsDay7 ?? a.avgViews ?? 0))
                .slice(0, 10)
                .map((p, i) => ({
                    position: i + 1,
                    programName: p.name,
                    instagramHandle: p.podcast?.socialAccounts?.instagram ?? '',
                }));
            return buildSocialPostRankingLines(items);
        }
        if (type.param === 'world-cup-livestream-programs' && rankingWorldCupLivestreamData?.ranking?.programRows) {
            const sorted = [...rankingWorldCupLivestreamData.ranking.programRows]
                .filter((row) => !isExcludedLivestreamRankingChannelName(row.sampleChannelName))
                .sort((a, b) => (b.metrics?.avgViewers ?? 0) - (a.metrics?.avgViewers ?? 0));
            return sorted.slice(0, 10).map((c, i) => `${i + 1}. ${c.programName}`).join('\n');
        }
        return '';
    }, [selectedRankingParam, programs, rankingChannelsData, rankingLivestreamData, rankingWorldCupViewsData, rankingWorldCupLivestreamData]);

    // Ranking lines for LinkedIn: same as above but without (@handle) on each line
    const socialPostRankingLinesLinkedIn = useMemo(() => {
        const type = RANKING_TYPES.find((t) => t.param === selectedRankingParam);
        if (!type) return '';
        if (type.param === 'views') {
            const items: SocialPostRankingItem[] = [...programs]
                .sort((a, b) => b.avgViews - a.avgViews)
                .slice(0, 10)
                .map((p, i) => ({ position: i + 1, programName: p.name, instagramHandle: p.podcast.socialAccounts?.instagram ?? '' }));
            return buildSocialPostRankingLines(items, { includeHandles: false });
        }
        if (type.param === 'channels-views') {
            return buildChannelsViewsRankingRows(programs)
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.name}`)
                .join('\n');
        }
        if (type.param === 'channels-interactions') {
            return buildChannelsInteractionsRankingRows(programs)
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.name}`)
                .join('\n');
        }
        if (type.param === 'interactions') {
            const items: SocialPostRankingItem[] = [...programs]
                .sort((a, b) => (b.avgInteractions ?? 0) - (a.avgInteractions ?? 0))
                .slice(0, 10)
                .map((p, i) => ({ position: i + 1, programName: p.name, instagramHandle: p.podcast.socialAccounts?.instagram ?? '' }));
            return buildSocialPostRankingLines(items, { includeHandles: false });
        }
        if (type.param === 'interactions-total') {
            const items: SocialPostRankingItem[] = [...programs]
                .sort((a, b) => (b.totalInteractionsDay7 ?? 0) - (a.totalInteractionsDay7 ?? 0))
                .slice(0, 10)
                .map((p, i) => ({ position: i + 1, programName: p.name, instagramHandle: p.podcast.socialAccounts?.instagram ?? '' }));
            return buildSocialPostRankingLines(items, { includeHandles: false });
        }
        if (type.param === 'programs-brands') {
            const items: SocialPostRankingItem[] = [...programs]
                .sort((a, b) => (b.uniquePaidBrandsCount ?? 0) - (a.uniquePaidBrandsCount ?? 0))
                .slice(0, 10)
                .map((p, i) => ({ position: i + 1, programName: p.name, instagramHandle: p.podcast.socialAccounts?.instagram ?? '' }));
            return buildSocialPostRankingLines(items, { includeHandles: false });
        }
        if (type.param === 'channels-social' && rankingChannelsData?.channels) {
            return rankingChannelsData.channels
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.name}`)
                .join('\n');
        }
        if (type.param === 'livestream-programs' && rankingLivestreamData?.channels) {
            const sorted = [...rankingLivestreamData.channels]
                .filter((c) => !isExcludedLivestreamRankingChannelName(c.channelName))
                .sort(
                    (a, b) => (b.metrics?.avgViewers ?? 0) - (a.metrics?.avgViewers ?? 0),
                );
            return sorted
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.channelName}`)
                .join('\n');
        }
        if (type.param === 'livestream-programs-program' && rankingLivestreamData?.ranking?.programRows) {
            const sorted = [...rankingLivestreamData.ranking.programRows]
                .filter((row) =>
                    !isExcludedLivestreamRankingChannelName(row.sampleChannelName),
                )
                .sort(
                    (a, b) => (b.metrics?.avgViewers ?? 0) - (a.metrics?.avgViewers ?? 0),
                );
            return sorted
                .slice(0, 10)
                .map((c, i) => `${i + 1}. ${c.programName}`)
                .join('\n');
        }
        if (type.param === 'world-cup-views' && rankingWorldCupViewsData?.programs) {
            const items: SocialPostRankingItem[] = [...rankingWorldCupViewsData.programs]
                .sort((a, b) => (b.avgViewsDay7 ?? b.avgViews ?? 0) - (a.avgViewsDay7 ?? a.avgViews ?? 0))
                .slice(0, 10)
                .map((p, i) => ({
                    position: i + 1,
                    programName: p.name,
                    instagramHandle: p.podcast?.socialAccounts?.instagram ?? '',
                }));
            return buildSocialPostRankingLines(items, { includeHandles: false });
        }
        if (type.param === 'world-cup-livestream-programs' && rankingWorldCupLivestreamData?.ranking?.programRows) {
            const sorted = [...rankingWorldCupLivestreamData.ranking.programRows]
                .filter((row) => !isExcludedLivestreamRankingChannelName(row.sampleChannelName))
                .sort((a, b) => (b.metrics?.avgViewers ?? 0) - (a.metrics?.avgViewers ?? 0));
            return sorted.slice(0, 10).map((c, i) => `${i + 1}. ${c.programName}`).join('\n');
        }
        return '';
    }, [selectedRankingParam, programs, rankingChannelsData, rankingLivestreamData, rankingWorldCupViewsData, rankingWorldCupLivestreamData]);

    // Full social post copy for LinkedIn: ranking without @handles, and no radar.podscope.co link
    const fullSocialPostCopy = useMemo(() => {
        const type = RANKING_TYPES.find((t) => t.param === selectedRankingParam);
        if (!type || !socialPostRankingLinesLinkedIn) return '';
        return buildSocialPostCopy(type.copyKey, rankingPeriodLabel, socialPostRankingLinesLinkedIn, {
            omitMoreInsightsLink: true,
        });
    }, [selectedRankingParam, rankingPeriodLabel, socialPostRankingLinesLinkedIn]);

    // IG copy: headline + howWeMeasure/source only (no ranking text)
    const fullSocialPostCopyIg = useMemo(() => {
        const type = RANKING_TYPES.find((t) => t.param === selectedRankingParam);
        if (!type) return '';
        return buildSocialPostCopy(type.copyKey, rankingPeriodLabel, '', { includeRanking: false });
    }, [selectedRankingParam, rankingPeriodLabel]);

    const rankingTypeSelectOptions = useMemo(() => {
        if (isExplorerLivestreamRankingParam(selectedRankingParam)) {
            return COMMUNITY_RANKING_TYPE_OPTIONS;
        }
        if (isWorldCupRankingParam(selectedRankingParam)) {
            return WORLD_CUP_RANKING_TYPE_OPTIONS;
        }
        const activeDimension = rankingStepIndexForParam(selectedRankingParam);
        return RANKING_TYPES.filter(
            (t) => rankingStepIndexForParam(t.param) === activeDimension,
        ).map((t) => ({ value: t.param, label: t.label }));
    }, [selectedRankingParam]);

    const rankingTypeSelectValue = isExplorerLivestreamRankingParam(selectedRankingParam)
        ? `${selectedRankingParam}|${livestreamExplorerCategory}`
        : selectedRankingParam;

    const openRankingAssetRender = (options?: {
        download?: boolean;
        ranking?: RankingParam;
        livestreamCategory?: 'all' | 'digital';
        sortOrder?: 'asc' | 'desc';
        includePodcastFilter?: boolean;
        tvDigitalPrograms?: boolean;
        mixDigitalTv?: boolean;
    }) => {
        const ranking = options?.ranking ?? selectedRankingParam;
        const dateParams = buildDateParams();
        const params = new URLSearchParams();
        params.set('ranking', ranking);
        if (dateParams.week) params.set('week', dateParams.week);
        if (dateParams.startDate) params.set('startDate', dateParams.startDate);
        if (dateParams.endDate) params.set('endDate', dateParams.endDate);
        params.set('type', PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE);
        if ((options?.includePodcastFilter ?? true) && selectedPodcastId !== 'all') {
            params.set('podcastId', selectedPodcastId);
        }
        if (isExplorerLivestreamRankingParam(ranking)) {
            params.set(
                'livestreamCategory',
                options?.livestreamCategory ?? livestreamExplorerCategory,
            );
        }
        if (isWorldCupRankingParam(ranking)) {
            params.set('worldCupOnly', 'true');
        }
        if (ranking === 'world-cup-livestream-programs') {
            params.set('livestreamGroup', 'programs');
        }
        const sortBy =
            ranking === 'views' || ranking === 'world-cup-views'
                ? 'avgViewsDay7'
                : ranking === 'channels-views'
                  ? 'totalViewsDay7'
                  : ranking === 'interactions'
                    ? 'avgInteractionsDay7'
                    : ranking === 'interactions-total'
                      ? 'totalInteractionsDay7'
                  : ranking === 'channels-interactions'
                    ? 'totalInteractionsDay7'
                  : ranking === 'programs-brands'
                    ? 'uniquePaidBrandsCount'
                    : 'avgViewsDay7';
        params.set('sortBy', sortBy);
        params.set('sortOrder', options?.sortOrder ?? order);
        if (minContentCount != null && minContentCount > 0) {
            params.set('minContentCount', String(minContentCount));
        }
        if (options?.tvDigitalPrograms) params.set('tvDigitalPrograms', '1');
        if (options?.mixDigitalTv) params.set('mixDigitalTv', '1');
        if (options?.download) params.set('download', '1');
        return `${window.location.origin}${ROUTES_NAMES.PROGRAM_ANALYSIS_RANKING_ASSET_RENDER}?${params.toString()}`;
    };

    const openRankingAssetInNewTab = (options?: Parameters<typeof openRankingAssetRender>[0]) => {
        window.open(openRankingAssetRender(options), '_blank', 'noopener,noreferrer');
    };

    const LINKEDIN_LIVESTREAM_IDS = [
        '6adea6372812a791cb37baa2',
        '6adea29b2812a791cb37baa1',
        '6adeb6fc2812a791cb37bc47',
        '6adeb6f42812a791cb37bc46',
        '6adeaade2812a791cb37bb75',
    ];

    const openLinkedInAssetInNewTab = (download?: boolean) => {
        const params = new URLSearchParams();
        params.set('ids', LINKEDIN_LIVESTREAM_IDS.join(','));
        if (download) params.set('download', '1');
        window.open(
            `${window.location.origin}${ROUTES_NAMES.PROGRAM_ANALYSIS_LINKEDIN_ASSET_RENDER}?${params.toString()}`,
            '_blank',
            'noopener,noreferrer',
        );
    };

    const LINKEDIN_LIVESTREAM_BRANDS_ID = '6ae3dc7b58951c7e4e599857';

    const openLinkedInLivestreamBrandsInNewTab = (download?: boolean) => {
        const params = new URLSearchParams();
        params.set('asset', 'marcas-vivo');
        params.set('id', LINKEDIN_LIVESTREAM_BRANDS_ID);
        if (download) params.set('download', '1');
        window.open(
            `${window.location.origin}${ROUTES_NAMES.PROGRAM_ANALYSIS_LINKEDIN_ASSET_RENDER}?${params.toString()}`,
            '_blank',
            'noopener,noreferrer',
        );
    };

    const openLinkedInRadiografiaInNewTab = (download?: boolean) => {
        const params = new URLSearchParams();
        params.set('asset', 'radiografia');
        if (download) params.set('download', '1');
        window.open(
            `${window.location.origin}${ROUTES_NAMES.PROGRAM_ANALYSIS_LINKEDIN_ASSET_RENDER}?${params.toString()}`,
            '_blank',
            'noopener,noreferrer',
        );
    };

    const openLinkedInInvestMapInNewTab = (download?: boolean) => {
        const params = new URLSearchParams();
        params.set('asset', 'donde-invertir');
        if (download) params.set('download', '1');
        window.open(
            `${window.location.origin}${ROUTES_NAMES.PROGRAM_ANALYSIS_LINKEDIN_ASSET_RENDER}?${params.toString()}`,
            '_blank',
            'noopener,noreferrer',
        );
    };

    const openLinkedInTvDigitalBrandsInNewTab = (download?: boolean) => {
        window.open(
            openRankingAssetRender({
                ranking: 'programs-brands',
                sortOrder: 'desc',
                includePodcastFilter: false,
                tvDigitalPrograms: true,
                download,
            }),
            '_blank',
            'noopener,noreferrer',
        );
    };

    const openLinkedInMixedDigitalTvBrandsInNewTab = (download?: boolean) => {
        window.open(
            openRankingAssetRender({
                ranking: 'programs-brands',
                sortOrder: 'desc',
                includePodcastFilter: false,
                mixDigitalTv: true,
                download,
            }),
            '_blank',
            'noopener,noreferrer',
        );
    };

    const downloadWeeklyIgAsset = (ranking: RankingParam) => {
        weeklyIgDownloadFrameRef.current?.remove();
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.title = 'Descarga de asset';
        iframe.style.cssText = [
            'position:fixed',
            'top:0',
            'left:0',
            'width:1080px',
            'height:1350px',
            'opacity:0',
            'pointer-events:none',
            'border:0',
            'z-index:-1',
        ].join(';');
        iframe.src = openRankingAssetRender({
            ranking,
            livestreamCategory: 'digital',
            sortOrder: 'desc',
            includePodcastFilter: false,
            download: true,
        });
        document.body.appendChild(iframe);
        weeklyIgDownloadFrameRef.current = iframe;
        setWeeklyIgDownloading(ranking);
    };

    const sendWeeklyIgEmail = async () => {
        const email = weeklyIgEmailTo.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setSnackbarMessage('Enter a valid email address');
            return;
        }
        const week =
            dateFilterMode === 'week'
            && selectedWeeks.length === 1
            && selectedWeeks[0] !== 'all'
            && /^\d{4}-\d{2}-\d{2}$/.test(selectedWeeks[0])
                ? selectedWeeks[0]
                : undefined;
        setWeeklyIgEmailSending(true);
        try {
            const result = await apiService.sendWeeklyIgEmail({
                to: email,
                ...(week ? { week } : {}),
            });
            setSnackbarMessage(result.message || `Weekly posts email sent to ${email}`);
        } catch (err: unknown) {
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data?.error
                || 'Failed to send weekly posts email';
            setSnackbarMessage(message);
        } finally {
            setWeeklyIgEmailSending(false);
        }
    };

    // Check if filtering by a single week (not 'all' and not multiple weeks)
    const isSingleWeekFilter = selectedWeeks.length === 1 && !selectedWeeks.includes('all');

    // Programs are sorted by backend
    const sortedPrograms = programs;

    const channelRows = useMemo(
        () => aggregateProgramsToChannelAnalysisRows(programs),
        [programs],
    );

    const sortedChannels = useMemo(
        () => sortChannelRows(channelRows, orderBy, order),
        [channelRows, orderBy, order],
    );

    const toggleMainTableChannelExpansion = (channelId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedChannels(prev => {
            const newSet = new Set(prev);
            if (newSet.has(channelId)) {
                newSet.delete(channelId);
            } else {
                newSet.add(channelId);
            }
            return newSet;
        });
    };

    // Calculate totals
    const totals = useMemo(() => {
        const totalPaidMentions =
            programsData?.totalPaidMentions ??
            programs.reduce((sum, p) => sum + (p.paidMentionsCount || 0), 0);
        const totalContentWithMentions =
            programsData?.totalContentWithMentions ??
            programs.reduce((sum, p) => sum + (p.contentWithMentionsCount || 0), 0);
        if (mainTableEntity === 'channels') {
            return {
                totalPrograms: sortedChannels.length,
                totalContent: sortedChannels.reduce((sum, c) => sum + c.contentCount, 0),
                totalViews: sortedChannels.reduce((sum, c) => sum + c.totalViews, 0),
                totalLikes: sortedChannels.reduce((sum, c) => sum + c.totalLikes, 0),
                totalComments: sortedChannels.reduce((sum, c) => sum + c.totalComments, 0),
                totalInteractions: sortedChannels.reduce((sum, c) => sum + c.totalInteractions, 0),
                totalUniqueBrands: sortedChannels.reduce((sum, c) => sum + c.uniquePaidBrandsCount, 0),
                totalPaidMentions,
                totalContentWithMentions,
                avgEngagement: sortedChannels.length > 0
                    ? sortedChannels.reduce((sum, c) => sum + c.engagementRate, 0) / sortedChannels.length
                    : 0,
                totalDurationMinutes: sortedChannels.reduce((sum, c) => sum + (c.totalDurationMinutes || 0), 0),
            };
        }
        return {
            totalPrograms: programs.length,
            totalContent: programs.reduce((sum, p) => sum + p.contentCount, 0),
            totalViews: programs.reduce((sum, p) => sum + p.totalViews, 0),
            totalLikes: programs.reduce((sum, p) => sum + p.totalLikes, 0),
            totalComments: programs.reduce((sum, p) => sum + p.totalComments, 0),
            totalInteractions: programs.reduce((sum, p) => sum + p.totalInteractions, 0),
            totalUniqueBrands: programsData?.totalUniqueBrands || 0,
            totalPaidMentions,
            totalContentWithMentions,
            avgEngagement: programs.length > 0
                ? programs.reduce((sum, p) => sum + p.engagementRate, 0) / programs.length
                : 0,
            totalDurationMinutes: programs.reduce((sum, p) => sum + (p.totalDurationMinutes || 0), 0),
        };
    }, [programs, sortedChannels, mainTableEntity, programsData?.totalUniqueBrands, programsData?.totalPaidMentions, programsData?.totalContentWithMentions]);

    const handleRequestSort = (property: string) => {
        // If clicking on a different column, default to 'desc' (greater on top)
        // If clicking on the same column, toggle between 'desc' and 'asc'
        if (orderBy === property) {
            // Same column - toggle
            setOrder(order === 'desc' ? 'asc' : 'desc');
        } else {
            // New column - default to 'desc' (greater on top)
            setOrderBy(property);
            setOrder('desc');
        }
    };

    const handleGuestRequestSort = (property: string) => {
        const isAsc = guestOrderBy === property && guestOrder === 'asc';
        setGuestOrder(isAsc ? 'desc' : 'asc');
        setGuestOrderBy(property);
    };

    const handleBrandRequestSort = (property: string) => {
        const isAsc = brandOrderBy === property && brandOrder === 'asc';
        setBrandOrder(isAsc ? 'desc' : 'asc');
        setBrandOrderBy(property);
    };

    const handleCategoryRankingsRequestSort = (property: string) => {
        const isAsc = categoryRankingsOrderBy === property && categoryRankingsOrder === 'asc';
        setCategoryRankingsOrder(isAsc ? 'desc' : 'asc');
        setCategoryRankingsOrderBy(property);
    };

    const toggleCategoryExpansion = (category: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    const toggleGuestExpansion = (guestId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedGuests(prev => {
            const newSet = new Set(prev);
            if (newSet.has(guestId)) {
                newSet.delete(guestId);
            } else {
                newSet.add(guestId);
            }
            return newSet;
        });
    };

    // Guest action menu handlers
    const handleGuestMenuOpen = (event: React.MouseEvent<HTMLElement>, guest: { _id: string; guestName: string }) => {
        event.stopPropagation();
        setGuestMenuAnchor(event.currentTarget);
        setSelectedGuestForAction(guest);
        setSelectedProgramForHost(null);
    };

    const handleGuestMenuClose = () => {
        setGuestMenuAnchor(null);
        setSelectedGuestForAction(null);
        setSelectedProgramForHost(null);
    };

    // Mutation for assigning guest as host
    const assignHostMutation = useMutation({
        mutationFn: async ({ guestId, programId }: { guestId: string; programId: string }) => {
            return apiService.assignGuestToProgram(guestId, programId, 'host');
        },
        onSuccess: (data) => {
            setSnackbarMessage(data.message || 'Guest assigned as host successfully');
            queryClient.invalidateQueries({ queryKey: ['guests-analytics'] });
            handleGuestMenuClose();
        },
        onError: (error: any) => {
            setSnackbarMessage(error?.response?.data?.error || 'Failed to assign guest as host');
        }
    });

    const handleAssignAsHost = () => {
        if (selectedGuestForAction && selectedProgramForHost) {
            assignHostMutation.mutate({
                guestId: selectedGuestForAction._id,
                programId: selectedProgramForHost._id
            });
        }
    };

    // Get all programs for the autocomplete
    const allPrograms = useMemo(() => {
        if (!programsData?.programs) return [];
        return programsData.programs.map(p => ({ _id: p._id, name: p.name }));
    }, [programsData?.programs]);

    const toggleBrandExpansion = (brandId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedBrands(prev => {
            const newSet = new Set(prev);
            if (newSet.has(brandId)) {
                newSet.delete(brandId);
            } else {
                newSet.add(brandId);
            }
            return newSet;
        });
    };

    const toggleBrandChannelExpansion = (brandId: string, podcastId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const key = `${brandId}-${podcastId}`;
        setExpandedBrandChannels(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const toggleCategoryBrandExpansion = (category: string, brandId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const key = `${category}::${brandId}`;
        setExpandedCategoryBrands(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleProgramClick = (program: ProgramAnalysisData, event: React.MouseEvent) => {
        // Don't navigate when on Programs tab (activeTab === 0)
        if (activeTab === 0) {
            return;
        }

        const url = `/podcast/${program.podcast._id}/content?programId=${program._id}`;

        if (event.metaKey || event.ctrlKey || event.button === 1) {
            window.open(url, '_blank');
        } else {
            navigate(url);
        }
    };

    const toggleProgramExpansion = (programId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedPrograms(prev => {
            const newSet = new Set(prev);
            if (newSet.has(programId)) {
                newSet.delete(programId);
            } else {
                newSet.add(programId);
            }
            return newSet;
        });
    };

    const formatNumber = (num: number | undefined | null): string => {
        if (num === undefined || num === null) return '0';
        if (num >= 1000000) {
            return `${(num / 1000000).toFixed(1)}M`;
        }
        if (num >= 1000) {
            return `${(num / 1000).toFixed(1)}K`;
        }
        return num.toString();
    };

    // Helper function to generate filter info for CSV filename
    const getFilterInfo = (): string[] => {
        const filterInfo: string[] = [];
        if (dateFilterMode === 'week' && !selectedWeeks.includes('all')) filterInfo.push(`weeks-${selectedWeeks.length}`);
        if (dateFilterMode === 'month' && !selectedWeeks.includes('all')) filterInfo.push(`months-${selectedWeeks.length}`);
        if (dateFilterMode === 'year' && !selectedWeeks.includes('all')) filterInfo.push(`years-${selectedWeeks.length}`);
        filterInfo.push(`category-${PROGRAM_ANALYSIS_ROUTE_CATEGORY}`);
        filterInfo.push(`type-${PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE}`);
        if (selectedPodcastId !== 'all') filterInfo.push('podcast-filtered');
        if (selectedProgramId !== 'all') filterInfo.push('program-filtered');
        if (minContentCount) filterInfo.push(`minContent-${minContentCount}`);
        return filterInfo;
    };

    const guests = useMemo(() => guestsData?.guests || [], [guestsData?.guests]);

    // Client-side sorting for guests
    // Backend now returns one row per guest-program combination
    const sortedGuests = useMemo(() => {
        // Add rowKey to each guest (now each guest entry is already per-program from backend)
        let processed = guests.map(guest => ({
            ...guest,
            rowKey: guest.program ? `${guest._id}-${guest.program._id}` : guest._id,
            // Filter out content items where isPodcast === false
            topContent: guest.topContent?.filter((content: any) => content.isPodcast !== false) || [],
        }));

        // Filter out hosts if excludeHosts is enabled
        if (excludeHosts) {
            processed = processed.filter(row =>
                !row.program || row.program.role !== 'host'
            );
        }

        const comparator = (a: any, b: any) => {
            let aValue: any;
            let bValue: any;

            switch (guestOrderBy) {
                case 'guestName':
                    aValue = a.guestName.toLowerCase();
                    bValue = b.guestName.toLowerCase();
                    break;
                case 'appearances':
                    aValue = a.appearances;
                    bValue = b.appearances;
                    break;
                case 'totalViews':
                    aValue = a.totalViews;
                    bValue = b.totalViews;
                    break;
                case 'avgViews':
                    aValue = a.avgViews;
                    bValue = b.avgViews;
                    break;
                case 'engagementRate':
                    aValue = a.engagementRate;
                    bValue = b.engagementRate;
                    break;
                case 'baselineAvgViews':
                    // Handle null values - put them at the end
                    aValue = a.baselineAvgViews ?? -Infinity;
                    bValue = b.baselineAvgViews ?? -Infinity;
                    break;
                case 'viewsImpact':
                    // Handle null values - put them at the end
                    aValue = a.viewsImpact ?? -Infinity;
                    bValue = b.viewsImpact ?? -Infinity;
                    break;
                default:
                    aValue = a.totalViews;
                    bValue = b.totalViews;
            }

            if (typeof aValue === 'string') {
                return guestOrder === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return guestOrder === 'asc' ? aValue - bValue : bValue - aValue;
        };

        return [...processed].sort(comparator);
    }, [guests, guestOrderBy, guestOrder, excludeHosts]);

    const brandsList = useMemo(() => brandsData?.brands || [], [brandsData?.brands]);

    // Comparator function for sorting brands (shared between sortedBrands and categoryRankings)
    const brandComparator = useMemo(() => {
        return (a: any, b: any) => {
            let aValue: any;
            let bValue: any;

            switch (brandOrderBy) {
                case 'name':
                    aValue = (a.brandName || a.name || '').toLowerCase();
                    bValue = (b.brandName || b.name || '').toLowerCase();
                    break;
                case 'programCount':
                    aValue = a.totalPrograms || a.programCount || 0;
                    bValue = b.totalPrograms || b.programCount || 0;
                    break;
                case 'totalPaidMentions':
                    aValue = a.totalPaidMentions;
                    bValue = b.totalPaidMentions;
                    break;
                case 'totalViews':
                    aValue = a.totalViews || 0;
                    bValue = b.totalViews || 0;
                    break;
                case 'totalAccumulatedViews':
                    aValue = a.totalAccumulatedViews || 0;
                    bValue = b.totalAccumulatedViews || 0;
                    break;
                default:
                    aValue = a.totalPrograms || a.programCount || 0;
                    bValue = b.totalPrograms || b.programCount || 0;
            }

            if (typeof aValue === 'string') {
                return brandOrder === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return brandOrder === 'asc' ? aValue - bValue : bValue - aValue;
        };
    }, [brandOrderBy, brandOrder]);

    // Client-side sorting and filtering for brands
    const sortedBrands = useMemo(() => {
        // Filter by category
        let filtered = [...brandsList];
        if (selectedBrandCategory !== 'all') {
            filtered = filtered.filter(brand => (brand.brandCategory || brand.category) === selectedBrandCategory);
        }
        // Filter by search query
        if (brandSearchQuery.trim()) {
            const query = brandSearchQuery.toLowerCase().trim();
            filtered = filtered.filter(brand =>
                brand.brandName?.toLowerCase().includes(query) ||
                brand.name?.toLowerCase().includes(query)
            );
        }

        return filtered.sort(brandComparator);
    }, [brandsList, brandComparator, selectedBrandCategory, brandSearchQuery]);

    // Fetch category rankings from backend
    const {
        data: categoryRankingsData,
        isLoading: categoryRankingsLoading,
    } = useQuery({
        queryKey: ['brand-category-rankings', { selectedWeeks, dateFilterMode, PROGRAM_ANALYSIS_ROUTE_CATEGORY, PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE, selectedPodcastId, minContentCount, categoryRankingsOrderBy, categoryRankingsOrder }],
        queryFn: () => {
            const dateParams = buildDateParams();
            return apiService.getBrandCategoryTopRankings(
                dateParams.week,
                podcastTaxonomy,
                selectedPodcastId !== 'all' ? selectedPodcastId : undefined,
                dateParams.year,
                dateParams.startDate,
                dateParams.endDate,
                minContentCount,
                categoryRankingsOrderBy,
                categoryRankingsOrder
            );
        },
        staleTime: 0,
        enabled: activeTab === 2 && brandsSubTab === 0, // Only fetch when brands tab is active and categories sub-tab is selected
    });

    // Filter category rankings based on brand search query
    const filteredCategoryRankings = useMemo(() => {
        const rankings = categoryRankingsData?.rankings || [];
        if (!brandSearchQuery.trim()) {
            return rankings;
        }
        const query = brandSearchQuery.toLowerCase().trim();
        return rankings
            .map((ranking: any) => {
                const filteredBrands = (ranking.allBrands || []).filter((brand: any) =>
                    brand.name?.toLowerCase().includes(query)
                );
                if (filteredBrands.length === 0) return null;
                return { ...ranking, allBrands: filteredBrands };
            })
            .filter(Boolean);
    }, [categoryRankingsData?.rankings, brandSearchQuery]);

    // Mutation to update brand category
    const updateBrandCategoryMutation = useMutation({
        mutationFn: ({ brandId, category }: { brandId: string; category: string | null }) =>
            apiService.updateBrandCategory(brandId, category),
        onSuccess: () => {
            // Invalidate and refetch both queries
            queryClient.invalidateQueries({ queryKey: ['brand-category-rankings'] });
            queryClient.invalidateQueries({ queryKey: ['brands-analytics-by-programs'] });
            setCategoryChangeAnchor(null);
            setSelectedNewCategory('');
            setSnackbarMessage('Brand category updated successfully');
        },
        onError: (error: any) => {
            setSnackbarMessage(error?.response?.data?.error || 'Failed to update brand category');
        }
    });

    const handleCategoryChangeClick = (event: React.MouseEvent<HTMLElement>, brandId: string, brandName: string) => {
        event.stopPropagation();
        setCategoryChangeAnchor({ element: event.currentTarget, brandId, brandName });
        setSelectedNewCategory('');
    };

    const handleCategoryChangeClose = () => {
        setCategoryChangeAnchor(null);
        setSelectedNewCategory('');
    };

    const handleCategoryChangeConfirm = () => {
        if (!categoryChangeAnchor) return;
        const category = selectedNewCategory === 'none' ? null : selectedNewCategory;
        updateBrandCategoryMutation.mutate({
            brandId: categoryChangeAnchor.brandId,
            category
        });
    };

    const handleBrandMergeMenuClick = (
        event: React.MouseEvent<HTMLElement>,
        brand: { _id: string; name?: string; brandName?: string; totalPaidMentions?: number; category?: string; brandCategory?: string },
    ) => {
        event.stopPropagation();
        setBrandMergeMenuAnchor({
            element: event.currentTarget,
            brand: {
                _id: brand._id,
                name: brand.brandName || brand.name || '',
                totalPaidMentions: brand.totalPaidMentions,
                category: brand.brandCategory || brand.category,
                brandCategory: brand.brandCategory || brand.category,
            },
        });
    };

    const handleBrandMergeMenuClose = () => {
        setBrandMergeMenuAnchor(null);
    };

    const handleBrandEditCategoryFromMenu = () => {
        if (!brandMergeMenuAnchor) return;
        const { element, brand } = brandMergeMenuAnchor;
        setBrandMergeMenuAnchor(null);
        setCategoryChangeAnchor({ element, brandId: brand._id, brandName: brand.name });
        const currentCategory = brand.brandCategory || brand.category;
        setSelectedNewCategory(currentCategory || 'none');
    };

    const handleBrandMergeDialogOpen = () => {
        if (!brandMergeMenuAnchor) return;
        setMergeDialogBrand(brandMergeMenuAnchor.brand);
        handleBrandMergeMenuClose();
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                py: 4,
                '& .MuiTypography-h1, & .MuiTypography-h2, & .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6, & .MuiTypography-subtitle1, & .MuiTypography-subtitle2': {
                    fontFamily: (theme) => theme.typography.fontFamily,
                    lineHeight: 1.35,
                },
            }}
        >
            <Box mb={4}>
                <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                    Rankings
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Analyze performance metrics for all programs with linked content
                </Typography>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(_e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Programs" />
                    <Tab label="Guests" />
                    <Tab label="Brands" />
                    <Tab label="Top Content" />
                    <Tab label="Time Series" />
                    <Tab label="Copa Mundial" />
                </Tabs>
            </Box>

            {/* Filters */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={dateFilterMode}
                                    label="Date Filter"
                                    onChange={(e) => {
                                        const newMode = e.target.value as 'week' | 'month' | 'year';
                                        setDateFilterMode(newMode);
                                        // Reset selection when switching modes; for week mode default to last week
                                        setSelectedWeeks(newMode === 'week' ? [lastWeekValue] : ['all']);
                                    }}
                                >
                                    <MenuItem value="week">By Week</MenuItem>
                                    <MenuItem value="month">By Month</MenuItem>
                                    <MenuItem value="year">By Year</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{dateFilterMode === 'week' ? 'Week' : dateFilterMode === 'month' ? 'Month' : 'Year'}</InputLabel>
                                <Select
                                    multiple
                                    value={selectedWeeks}
                                    label={dateFilterMode === 'week' ? 'Week' : dateFilterMode === 'month' ? 'Month' : 'Year'}
                                    onChange={(e) => {
                                        const value = e.target.value as string[];
                                        // If 'all' is selected, clear other selections
                                        if (value.includes('all') && !selectedWeeks.includes('all')) {
                                            setSelectedWeeks(['all']);
                                        }
                                        // If other periods are selected while 'all' is active, remove 'all'
                                        else if (value.includes('all') && value.length > 1) {
                                            setSelectedWeeks(value.filter(v => v !== 'all'));
                                        }
                                        // Normal multi-select behavior
                                        else {
                                            setSelectedWeeks(value.length === 0 ? ['all'] : value);
                                        }
                                    }}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map((value) => {
                                                const options = dateFilterMode === 'week' ? weekOptions : dateFilterMode === 'month' ? monthOptions : yearOptions;
                                                const option = options.find(w => w.value === value);
                                                return (
                                                    <Chip
                                                        key={value}
                                                        label={option?.label || value}
                                                        size="small"
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                >
                                    {(dateFilterMode === 'week' ? weekOptions : dateFilterMode === 'month' ? monthOptions : yearOptions).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
                                <Chip size="small" label="Other" color="default" />
                                <Chip size="small" label="Digital" color="default" />
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Podcast</InputLabel>
                                <Select
                                    value={selectedPodcastId}
                                    label="Podcast"
                                    onChange={(e) => setSelectedPodcastId(e.target.value)}
                                >
                                    <MenuItem value="all">All Podcasts</MenuItem>
                                    {podcasts.map((podcast) => (
                                        <MenuItem key={podcast._id} value={podcast._id}>
                                            {podcast.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                    </Grid>
                </CardContent>
            </Card>

            {/* Program Analysis Tab Content */}
            {activeTab === 0 && (
                <>
                    <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch' }}>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    {/* Ranking assets section — layout: header + divider + controls (Figma 9303:2513) */}
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            borderColor: 'divider',
                            width: '100%',
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Stack spacing={0}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                        pb: 2.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 1.5,
                                            bgcolor: 'action.selected',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'primary.main',
                                            flexShrink: 0,
                                        }}
                                        aria-hidden
                                    >
                                        <BarChartIcon sx={{ fontSize: 24 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                color: 'text.secondary',
                                                letterSpacing: '0.06em',
                                                fontWeight: 600,
                                                lineHeight: 1.2,
                                                display: 'block',
                                                mb: 0.5,
                                            }}
                                        >
                                            Export
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            component="h2"
                                            fontWeight={600}
                                            sx={{
                                                letterSpacing: '-0.01em',
                                                mb: 1,
                                                color: 'text.primary',
                                            }}
                                        >
                                            Ranking assets
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ maxWidth: 720, lineHeight: 1.57, mb: 2 }}
                                        >
                                            Generate shareable ranking assets using the current filters. Open the render in a
                                            new tab or download PNG (development only).
                                        </Typography>
                                        <RankingDimensionStepper
                                            selectedRankingParam={selectedRankingParam}
                                            onSelectRankingParam={setSelectedRankingParam}
                                        />
                                    </Box>
                                </Box>
                                <Divider sx={{ borderColor: 'divider' }} />
                                <Stack spacing={2} sx={{ pt: 2.5 }}>
                                    <FormControl size="small" sx={{ maxWidth: { xs: '100%', sm: 360 } }} fullWidth>
                                        <InputLabel>Ranking type</InputLabel>
                                        <Select
                                            value={rankingTypeSelectValue}
                                            label="Ranking type"
                                            onChange={(e) => {
                                                const value = e.target.value as string;
                                                if (value.includes('|')) {
                                                    const [param, category] = value.split('|') as [
                                                        RankingParam,
                                                        'all' | 'digital',
                                                    ];
                                                    setSelectedRankingParam(param);
                                                    setLivestreamExplorerCategory(category);
                                                } else {
                                                    setSelectedRankingParam(value as RankingParam);
                                                }
                                            }}
                                        >
                                            {rankingTypeSelectOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                <Button
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={() => openRankingAssetInNewTab()}
                                >
                                    Ver asset
                                </Button>
                                {process.env.NODE_ENV === 'development' && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<ImageIcon />}
                                        onClick={() => openRankingAssetInNewTab({ download: true })}
                                    >
                                        Download PNG
                                    </Button>
                                )}
                                <Tooltip
                                    title={
                                        fullSocialPostCopyIg ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    display: 'block',
                                                    maxWidth: 420,
                                                    maxHeight: 420,
                                                    overflow: 'auto',
                                                    py: 0.5,
                                                    px: 0.5,
                                                }}
                                            >
                                                {fullSocialPostCopyIg}
                                            </Box>
                                        ) : (
                                            ''
                                        )
                                    }
                                    placement="top-start"
                                    slotProps={{ popper: { sx: { maxWidth: 460 } } }}
                                >
                                    <span>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ContentCopyIcon />}
                                            onClick={() => {
                                                if (!fullSocialPostCopyIg) return;
                                                navigator.clipboard.writeText(fullSocialPostCopyIg).then(() => setSnackbarMessage('Copied to clipboard'));
                                            }}
                                            disabled={!fullSocialPostCopyIg}
                                        >
                                            Copy Instagram copy
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        fullSocialPostCopy ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    display: 'block',
                                                    maxWidth: 420,
                                                    maxHeight: 420,
                                                    overflow: 'auto',
                                                    py: 0.5,
                                                    px: 0.5,
                                                }}
                                            >
                                                {fullSocialPostCopy}
                                            </Box>
                                        ) : (
                                            ''
                                        )
                                    }
                                    placement="top-start"
                                    slotProps={{ popper: { sx: { maxWidth: 460 } } }}
                                >
                                    <span>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ContentCopyIcon />}
                                            onClick={() => {
                                                if (!fullSocialPostCopy) return;
                                                navigator.clipboard.writeText(fullSocialPostCopy).then(() => setSnackbarMessage('Copied to clipboard'));
                                            }}
                                            disabled={!socialPostRankingLinesLinkedIn}
                                        >
                                            Copy LinkedIn copy
                                        </Button>
                                    </span>
                                </Tooltip>
                                    </Box>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            borderColor: 'divider',
                            width: '100%',
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: 'text.secondary',
                                    letterSpacing: '0.06em',
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    display: 'block',
                                    mb: 0.5,
                                }}
                            >
                                Instagram
                            </Typography>
                            <Typography
                                variant="h6"
                                component="h2"
                                fontWeight={600}
                                sx={{ letterSpacing: '-0.01em', mb: 2 }}
                            >
                                Weekly posts
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <TextField
                                    size="small"
                                    type="email"
                                    label="Send via email"
                                    value={weeklyIgEmailTo}
                                    onChange={(e) => setWeeklyIgEmailTo(e.target.value)}
                                    disabled={weeklyIgEmailSending}
                                    sx={{ minWidth: 220, flex: '1 1 180px' }}
                                />
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={
                                        weeklyIgEmailSending
                                            ? <CircularProgress size={16} color="inherit" />
                                            : <SendIcon />
                                    }
                                    disabled={weeklyIgEmailSending || !weeklyIgEmailTo.trim()}
                                    onClick={() => void sendWeeklyIgEmail()}
                                >
                                    {weeklyIgEmailSending ? 'Sending…' : 'Send'}
                                </Button>
                            </Box>
                            <Stack spacing={1.5}>
                                {WEEKLY_IG_RANKINGS.map((item) => {
                                    const template = weeklyIgCopyDrafts[item.param] ?? item.copy;
                                    const filled = weeklyIgFilledCopy(item);
                                    const isEditing = weeklyIgCopyEditing === item.param;
                                    const isEdited = weeklyIgCopyDrafts[item.param] != null
                                        && weeklyIgCopyDrafts[item.param] !== item.copy;
                                    const openAsset = () =>
                                        openRankingAssetInNewTab({
                                            ranking: item.param,
                                            livestreamCategory: 'digital',
                                            sortOrder: 'desc',
                                            includePodcastFilter: false,
                                        });
                                    return (
                                        <Box key={item.param}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ minWidth: 0 }}>
                                                    {item.label}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<OpenInNewIcon />}
                                                        onClick={() => openAsset()}
                                                    >
                                                        Ver asset
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={
                                                            weeklyIgDownloading === item.param
                                                                ? <CircularProgress size={16} />
                                                                : <DownloadIcon />
                                                        }
                                                        disabled={weeklyIgDownloading != null}
                                                        onClick={() => downloadWeeklyIgAsset(item.param)}
                                                    >
                                                        {weeklyIgDownloading === item.param ? 'Descargando…' : 'Descargar asset'}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant={isEditing ? 'contained' : 'outlined'}
                                                        startIcon={<EditIcon />}
                                                        onClick={() =>
                                                            setWeeklyIgCopyEditing((current) =>
                                                                current === item.param ? null : item.param,
                                                            )
                                                        }
                                                    >
                                                        Edit copy
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<ContentCopyIcon />}
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(filled).then(() =>
                                                                setSnackbarMessage('Copied to clipboard'),
                                                            );
                                                        }}
                                                    >
                                                        Copy copy
                                                    </Button>
                                                </Box>
                                            </Box>
                                            {isEditing && (
                                                <Box sx={{ mt: 1 }}>
                                                    <TextField
                                                        value={template}
                                                        onChange={(e) =>
                                                            setWeeklyIgCopyDrafts((prev) => ({
                                                                ...prev,
                                                                [item.param]: e.target.value,
                                                            }))
                                                        }
                                                        multiline
                                                        minRows={6}
                                                        fullWidth
                                                        size="small"
                                                        helperText="{dateRange}, {programCount}, and {channelCount} fill in when you copy."
                                                    />
                                                    {isEdited && (
                                                        <Button
                                                            size="small"
                                                            sx={{ mt: 0.5 }}
                                                            onClick={() =>
                                                                setWeeklyIgCopyDrafts((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[item.param];
                                                                    return next;
                                                                })
                                                            }
                                                        >
                                                            Reset
                                                        </Button>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </CardContent>
                    </Card>
                    </Grid>
                    </Grid>

                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: 'divider',
                            mb: 3,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.selected',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        flexShrink: 0,
                                    }}
                                    aria-hidden
                                >
                                    <LinkedInIcon sx={{ fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            color: 'text.secondary',
                                            letterSpacing: '0.06em',
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        LinkedIn
                                    </Typography>
                                    <Typography variant="h6" component="h2" fontWeight={600} sx={{ letterSpacing: '-0.01em', mb: 0.5 }}>
                                        Livestreams
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                                        Asset de LinkedIn que compara tres transmisiones minuto a minuto, con promedio, peak y views al día 7.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() => openLinkedInAssetInNewTab()}
                                        >
                                            Ver asset
                                        </Button>
                                        {process.env.NODE_ENV === 'development' && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImageIcon />}
                                                onClick={() => openLinkedInAssetInNewTab(true)}
                                            >
                                                Download PNG
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: 'divider',
                            mb: 3,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.selected',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        flexShrink: 0,
                                    }}
                                    aria-hidden
                                >
                                    <LinkedInIcon sx={{ fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            color: 'text.secondary',
                                            letterSpacing: '0.06em',
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        LinkedIn
                                    </Typography>
                                    <Typography variant="h6" component="h2" fontWeight={600} sx={{ letterSpacing: '-0.01em', mb: 0.5 }}>
                                        Marcas en vivo
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                                        Asset de LinkedIn que diseca la presencia de marcas en un livestream: curva minuto a minuto y consolidado por mención, placement, banner y créditos.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() => openLinkedInLivestreamBrandsInNewTab()}
                                        >
                                            Ver asset
                                        </Button>
                                        {process.env.NODE_ENV === 'development' && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImageIcon />}
                                                onClick={() => openLinkedInLivestreamBrandsInNewTab(true)}
                                            >
                                                Download PNG
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: 'divider',
                            mb: 3,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.selected',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        flexShrink: 0,
                                    }}
                                    aria-hidden
                                >
                                    <LinkedInIcon sx={{ fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            color: 'text.secondary',
                                            letterSpacing: '0.06em',
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        LinkedIn
                                    </Typography>
                                    <Typography variant="h6" component="h2" fontWeight={600} sx={{ letterSpacing: '-0.01em', mb: 0.5 }}>
                                        Radiografía de marca
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                                        Asset de LinkedIn con la presencia de Coca-Cola día a día (19–25 oct) y métricas consolidadas por canal.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() => openLinkedInRadiografiaInNewTab()}
                                        >
                                            Ver asset
                                        </Button>
                                        {process.env.NODE_ENV === 'development' && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImageIcon />}
                                                onClick={() => openLinkedInRadiografiaInNewTab(true)}
                                            >
                                                Download PNG
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: 'divider',
                            mb: 3,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.selected',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        flexShrink: 0,
                                    }}
                                    aria-hidden
                                >
                                    <LinkedInIcon sx={{ fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            color: 'text.secondary',
                                            letterSpacing: '0.06em',
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        LinkedIn
                                    </Typography>
                                    <Typography variant="h6" component="h2" fontWeight={600} sx={{ letterSpacing: '-0.01em', mb: 0.5 }}>
                                        Programas digitales: ¿Dónde está la audiencia?
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                                        Tabla top 10 de programas 100% digitales (5–11 oct) por views a 7 días, con interacción y audiencia en vivo.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() => openLinkedInInvestMapInNewTab()}
                                        >
                                            Ver asset
                                        </Button>
                                        {process.env.NODE_ENV === 'development' && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImageIcon />}
                                                onClick={() => openLinkedInInvestMapInNewTab(true)}
                                            >
                                                Download PNG
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: 'divider',
                            mb: 3,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.selected',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        flexShrink: 0,
                                    }}
                                    aria-hidden
                                >
                                    <LinkedInIcon sx={{ fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            color: 'text.secondary',
                                            letterSpacing: '0.06em',
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        LinkedIn
                                    </Typography>
                                    <Typography variant="h6" component="h2" fontWeight={600} sx={{ letterSpacing: '-0.01em', mb: 0.5 }}>
                                        Programas por marcas · TV digital
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                                        Mismo ranking que Programas por marcas, solo con programas 100% digitales de canales de TV, en el período seleccionado.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() => openLinkedInTvDigitalBrandsInNewTab()}
                                        >
                                            Ver asset
                                        </Button>
                                        {process.env.NODE_ENV === 'development' && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImageIcon />}
                                                onClick={() => openLinkedInTvDigitalBrandsInNewTab(true)}
                                            >
                                                Download PNG
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: 'divider',
                            mb: 3,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 1.5,
                                        bgcolor: 'action.selected',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.main',
                                        flexShrink: 0,
                                    }}
                                    aria-hidden
                                >
                                    <LinkedInIcon sx={{ fontSize: 24 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            color: 'text.secondary',
                                            letterSpacing: '0.06em',
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        LinkedIn
                                    </Typography>
                                    <Typography variant="h6" component="h2" fontWeight={600} sx={{ letterSpacing: '-0.01em', mb: 0.5 }}>
                                        Programas por marcas · Digital y TV
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                                        Mismo ranking que Programas por marcas, con canales 100% digitales y programas 100% digitales de canales de TV, en el período seleccionado.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() => openLinkedInMixedDigitalTvBrandsInNewTab()}
                                        >
                                            Ver asset
                                        </Button>
                                        {process.env.NODE_ENV === 'development' && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImageIcon />}
                                                onClick={() => openLinkedInMixedDigitalTvBrandsInNewTab(true)}
                                            >
                                                Download PNG
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Export CSV + entity toggle */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <ToggleButtonGroup
                            value={mainTableEntity}
                            exclusive
                            size="small"
                            onChange={(_e, val) => { if (val) setMainTableEntity(val); }}
                        >
                            <ToggleButton value="programs">Programas</ToggleButton>
                            <ToggleButton value="channels">Canales</ToggleButton>
                        </ToggleButtonGroup>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                                const filterInfo = [];
                                if (dateFilterMode === 'week' && !selectedWeeks.includes('all')) filterInfo.push(`weeks-${selectedWeeks.length}`);
                                if (dateFilterMode === 'month' && !selectedWeeks.includes('all')) filterInfo.push(`months-${selectedWeeks.length}`);
                                if (dateFilterMode === 'year' && !selectedWeeks.includes('all')) filterInfo.push(`years-${selectedWeeks.length}`);
                                filterInfo.push(`category-${PROGRAM_ANALYSIS_ROUTE_CATEGORY}`);
                                filterInfo.push(`type-${PROGRAM_ANALYSIS_ROUTE_CHANNEL_TYPE}`);
                                if (selectedPodcastId !== 'all') filterInfo.push('podcast-filtered');
                                if (minContentCount) filterInfo.push(`minContent-${minContentCount}`);

                                if (mainTableEntity === 'channels') {
                                    const csvData = sortedChannels.map((channel, index) => ({
                                        Rank: index + 1,
                                        Channel: channel.name,
                                        Programs: channel.programCount,
                                        'Content Count': channel.contentCount,
                                        'Total Views': channel.totalViews,
                                        'Avg Views': channel.avgViews.toFixed(2),
                                        'Avg Views 7D': (channel.avgViewsDay7 ?? 0).toFixed(2),
                                        'Total Views 7D': (channel.totalViewsDay7 ?? 0).toFixed(0),
                                        'Total Avg Views': (channel.avgViewsAllTime || 0).toFixed(2),
                                        'Total Likes': channel.totalLikes,
                                        'Total Comments': channel.totalComments,
                                        'Total Interactions': channel.totalInteractions,
                                        'Avg Interactions': channel.avgInteractions.toFixed(2),
                                        'Avg Int. 7D': (channel.avgInteractionsDay7 ?? 0).toFixed(2),
                                        'Total Int. 7D': (channel.totalInteractionsDay7 ?? 0).toFixed(0),
                                        'Engagement Rate': `${channel.engagementRate.toFixed(2)}%`,
                                        'Unique Paid Brands': channel.uniquePaidBrandsCount,
                                        'Date Range': `${channel.dateRange.earliest ? new Date(channel.dateRange.earliest).toLocaleDateString() : 'N/A'} - ${channel.dateRange.latest ? new Date(channel.dateRange.latest).toLocaleDateString() : 'N/A'}`,
                                    }));
                                    const csvContent = convertToCSV(csvData);
                                    const filename = createTimestampedFilename(`channels-analysis${filterInfo.length > 0 ? `-${filterInfo.join('-')}` : ''}`);
                                    downloadCSV(csvContent, filename);
                                } else {
                                    const csvData = sortedPrograms.map((program, index) => ({
                                        Rank: index + 1,
                                        Program: program.name,
                                        Podcast: program.podcast.title,
                                        'Content Count': program.contentCount,
                                        'Total Views': program.totalViews,
                                        'Avg Views': program.avgViews.toFixed(2),
                                        'Avg Views 7D': (program.avgViewsDay7 ?? 0).toFixed(2),
                                        'Total Views 7D': (program.totalViewsDay7 ?? 0).toFixed(0),
                                        'Total Avg Views': (program.avgViewsAllTime || 0).toFixed(2),
                                        'Total Likes': program.totalLikes,
                                        'Total Comments': program.totalComments,
                                        'Total Interactions': program.totalInteractions,
                                        'Avg Interactions': program.avgInteractions.toFixed(2),
                                        'Avg Int. 7D': (program.avgInteractionsDay7 ?? 0).toFixed(2),
                                        'Total Int. 7D': (program.totalInteractionsDay7 ?? 0).toFixed(0),
                                        'Engagement Rate': `${program.engagementRate.toFixed(2)}%`,
                                        'Unique Paid Brands': program.uniquePaidBrandsCount,
                                        'Date Range': `${program.dateRange.earliest ? new Date(program.dateRange.earliest).toLocaleDateString() : 'N/A'} - ${program.dateRange.latest ? new Date(program.dateRange.latest).toLocaleDateString() : 'N/A'}`,
                                    }));
                                    const csvContent = convertToCSV(csvData);
                                    const filename = createTimestampedFilename(`programs-analysis${filterInfo.length > 0 ? `-${filterInfo.join('-')}` : ''}`);
                                    downloadCSV(csvContent, filename);
                                }
                            }}
                        >
                            Export CSV
                        </Button>
                    </Box>
                    {/* Summary Stats */}
                    <Grid container spacing={3} sx={{ mb: 4 }} columns={{ xs: 12, sm: 12, md: 10 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" color="primary">
                                        {totals.totalPrograms}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {mainTableEntity === 'channels' ? 'Channels' : 'Programs'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" color="secondary">
                                        {totals.totalContent}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Content
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" color="success.main">
                                        {formatNumber(totals.totalViews)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Views
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ color: '#2196F3' }}>
                                        {formatNumber(totals.totalInteractions)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Interactions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ color: '#9C27B0' }}>
                                        {totals.totalUniqueBrands}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Paid Brands
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ color: '#7B1FA2' }}>
                                        {totals.totalPaidMentions.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Paid Mentions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ color: '#6A1B9A' }}>
                                        {totals.totalContentWithMentions.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Content w/ Mentions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" color="error.main">
                                        {totals.avgEngagement.toFixed(2)}%
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Avg Engagement
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ color: '#FF9800' }}>
                                        {totals.totalDurationMinutes >= 60
                                            ? `${(totals.totalDurationMinutes / 60).toFixed(1)}h`
                                            : `${totals.totalDurationMinutes}m`
                                        }
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Duration
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ color: '#4CAF50' }}>
                                        {formatNumber(totals.totalComments)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Comments
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Table */}
                    {error ? (
                        <Alert severity="error">Failed to load program analysis data</Alert>
                    ) : (
                        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                            <Table
                                sx={{
                                    width: '100%',
                                    tableLayout: 'fixed',
                                    // Floor so columns stay readable; container scrolls on narrow viewports.
                                    minWidth: 1000,
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: '3%' }} />
                                        <TableCell align="center" sx={{ width: '3%' }}>#</TableCell>
                                        <TableCell sx={{ width: '22%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'name'}
                                                direction={orderBy === 'name' ? order : 'asc'}
                                                onClick={() => handleRequestSort('name')}
                                            >
                                                {mainTableEntity === 'channels' ? 'Canal' : 'Program'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={{ width: '11%' }}>
                                            <TableSortLabel
                                                active={mainTableEntity === 'channels' ? orderBy === 'programCount' : orderBy === 'podcast'}
                                                direction={(mainTableEntity === 'channels' ? orderBy === 'programCount' : orderBy === 'podcast') ? order : 'asc'}
                                                onClick={() => handleRequestSort(mainTableEntity === 'channels' ? 'programCount' : 'podcast')}
                                            >
                                                {mainTableEntity === 'channels' ? 'Programas' : 'Podcast'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'contentCount'}
                                                direction={orderBy === 'contentCount' ? order : 'asc'}
                                                onClick={() => handleRequestSort('contentCount')}
                                            >
                                                Content
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'totalViews'}
                                                direction={orderBy === 'totalViews' ? order : 'asc'}
                                                onClick={() => handleRequestSort('totalViews')}
                                            >
                                                Total Views
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'avgViews'}
                                                direction={orderBy === 'avgViews' ? order : 'asc'}
                                                onClick={() => handleRequestSort('avgViews')}
                                            >
                                                Avg Views
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'avgViewsDay7'}
                                                direction={orderBy === 'avgViewsDay7' ? order : 'asc'}
                                                onClick={() => handleRequestSort('avgViewsDay7')}
                                            >
                                                Avg Views 7D
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'totalViewsDay7'}
                                                direction={orderBy === 'totalViewsDay7' ? order : 'asc'}
                                                onClick={() => handleRequestSort('totalViewsDay7')}
                                            >
                                                Total Views 7D
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'avgViewsAllTime'}
                                                direction={orderBy === 'avgViewsAllTime' ? order : 'asc'}
                                                onClick={() => handleRequestSort('avgViewsAllTime')}
                                            >
                                                Total Avg Views
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'avgInteractions'}
                                                direction={orderBy === 'avgInteractions' ? order : 'asc'}
                                                onClick={() => handleRequestSort('avgInteractions')}
                                            >
                                                Avg Int.
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'avgInteractionsDay7'}
                                                direction={orderBy === 'avgInteractionsDay7' ? order : 'asc'}
                                                onClick={() => handleRequestSort('avgInteractionsDay7')}
                                            >
                                                Avg Int. 7D
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'totalInteractionsDay7'}
                                                direction={orderBy === 'totalInteractionsDay7' ? order : 'asc'}
                                                onClick={() => handleRequestSort('totalInteractionsDay7')}
                                            >
                                                Total Int. 7D
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '4.5%' }}>
                                            <TableSortLabel
                                                active={orderBy === 'uniquePaidBrandsCount'}
                                                direction={orderBy === 'uniquePaidBrandsCount' ? order : 'asc'}
                                                onClick={() => handleRequestSort('uniquePaidBrandsCount')}
                                            >
                                                Paid Brands
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading && programs.length === 0 ? (
                                        // Skeleton rows
                                        Array.from({ length: 10 }).map((_, index) => (
                                            <TableRow key={`skeleton-${index}`}>
                                                <TableCell sx={{ width: '3%' }}>
                                                    <Skeleton variant="circular" width={24} height={24} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '3%' }}>
                                                    <Skeleton variant="text" width={30} />
                                                </TableCell>
                                                <TableCell sx={{ width: '22%' }}>
                                                    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                                        <Skeleton variant="circular" width={40} height={40} />
                                                        <Skeleton variant="text" width="100%" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ width: '11%' }}>
                                                    <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                                                        <Skeleton variant="circular" width={22} height={22} />
                                                        <Skeleton variant="text" width="100%" />
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={30} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={40} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={40} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={40} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={40} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={40} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={40} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={40} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '5%' }}>
                                                    <Skeleton variant="text" width={40} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: '4.5%' }}>
                                                    <Skeleton variant="text" width={30} />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (mainTableEntity === 'channels' ? sortedChannels.length === 0 : sortedPrograms.length === 0) ? (
                                        <TableRow>
                                            <TableCell colSpan={14} align="center">
                                                <Typography variant="body2" color="text.secondary" py={4}>
                                                    {mainTableEntity === 'channels'
                                                        ? 'No channels found with the selected filters'
                                                        : 'No programs found with the selected filters'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : mainTableEntity === 'channels' ? (
                                        sortedChannels.map((channel, index) => (
                                            <ChannelRow
                                                key={channel._id}
                                                channel={channel}
                                                index={index}
                                                isExpanded={expandedChannels.has(channel._id)}
                                                onToggleExpand={toggleMainTableChannelExpansion}
                                                formatNumber={formatNumber}
                                                selectedWeeks={selectedWeeks}
                                                isSingleWeekFilter={isSingleWeekFilter}
                                            />
                                        ))
                                    ) : (
                                        sortedPrograms.map((program, index) => (
                                            <ProgramRow
                                                key={program._id}
                                                program={program}
                                                index={index}
                                                isExpanded={expandedPrograms.has(program._id)}
                                                onToggleExpand={toggleProgramExpansion}
                                                onProgramClick={handleProgramClick}
                                                formatNumber={formatNumber}
                                                selectedWeeks={selectedWeeks}
                                                programBrandsDateQuery={programBrandsDateQuery}
                                                isSingleWeekFilter={isSingleWeekFilter}
                                                isClickable={activeTab !== 0}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                </>
            )}

            {/* Guest Analytics Tab Content */}
            {activeTab === 1 && (
                <>
                    {/* Export Button */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                                const csvData = sortedGuests.map((guest, index) => ({
                                    Rank: index + 1,
                                    'Guest Name': guest.guestName,
                                    Program: guest.program?.name || 'N/A',
                                    'Program Role': guest.program?.role || 'guest',
                                    Podcast: guest.podcast?.title || 'N/A',
                                    Appearances: guest.appearances,
                                    'Total Views': guest.totalViews,
                                    'Avg Views': guest.avgViews.toFixed(2),
                                    'Total Likes': guest.totalLikes,
                                    'Total Comments': guest.totalComments,
                                    'Engagement Rate': `${guest.engagementRate.toFixed(2)}%`,
                                    '4W Avg Views': guest.baselineAvgViews ? guest.baselineAvgViews.toFixed(2) : 'N/A',
                                    'Views Impact': guest.viewsImpact ? `${guest.viewsImpact.toFixed(2)}%` : 'N/A',
                                }));

                                const filterInfo = getFilterInfo();
                                if (excludeHosts) filterInfo.push('exclude-hosts');

                                const csvContent = convertToCSV(csvData);
                                const filename = createTimestampedFilename(`guests-analysis${filterInfo.length > 0 ? `-${filterInfo.join('-')}` : ''}`);
                                downloadCSV(csvContent, filename);
                            }}
                        >
                            Export CSV
                        </Button>
                    </Box>
                    {/* Guest Filters */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Show</InputLabel>
                                        <Select
                                            value={excludeHosts ? 'guests_only' : 'all'}
                                            label="Show"
                                            onChange={(e) => setExcludeHosts(e.target.value === 'guests_only')}
                                        >
                                            <MenuItem value="all">All People</MenuItem>
                                            <MenuItem value="guests_only">Guests Only (Exclude Hosts)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {guestsError ? (
                        <Alert severity="error">Failed to load guest analytics data</Alert>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 50 }} />
                                        <TableCell align="center">#</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={guestOrderBy === 'guestName'}
                                                direction={guestOrderBy === 'guestName' ? guestOrder : 'asc'}
                                                onClick={() => handleGuestRequestSort('guestName')}
                                            >
                                                Guest Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            Program
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={guestOrderBy === 'appearances'}
                                                direction={guestOrderBy === 'appearances' ? guestOrder : 'asc'}
                                                onClick={() => handleGuestRequestSort('appearances')}
                                            >
                                                Appearances
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={guestOrderBy === 'totalViews'}
                                                direction={guestOrderBy === 'totalViews' ? guestOrder : 'asc'}
                                                onClick={() => handleGuestRequestSort('totalViews')}
                                            >
                                                Total Views
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={guestOrderBy === 'avgViews'}
                                                direction={guestOrderBy === 'avgViews' ? guestOrder : 'asc'}
                                                onClick={() => handleGuestRequestSort('avgViews')}
                                            >
                                                7D Avg Views
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={guestOrderBy === 'engagementRate'}
                                                direction={guestOrderBy === 'engagementRate' ? guestOrder : 'asc'}
                                                onClick={() => handleGuestRequestSort('engagementRate')}
                                            >
                                                Engagement %
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={guestOrderBy === 'baselineAvgViews'}
                                                direction={guestOrderBy === 'baselineAvgViews' ? guestOrder : 'asc'}
                                                onClick={() => handleGuestRequestSort('baselineAvgViews')}
                                            >
                                                4W Avg Views
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TableSortLabel
                                                active={guestOrderBy === 'viewsImpact'}
                                                direction={guestOrderBy === 'viewsImpact' ? guestOrder : 'asc'}
                                                onClick={() => handleGuestRequestSort('viewsImpact')}
                                            >
                                                Views Impact %
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: 60 }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {guestsLoading ? (
                                        Array.from({ length: 10 }).map((_, index) => (
                                            <TableRow key={`skeleton-${index}`}>
                                                <TableCell>
                                                    <Skeleton variant="circular" width={24} height={24} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="text" width={30} />
                                                </TableCell>
                                                <TableCell>
                                                    <Skeleton variant="text" width={200} />
                                                </TableCell>
                                                <TableCell>
                                                    <Skeleton variant="rectangular" width={300} height={32} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="text" width={60} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="text" width={80} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="text" width={80} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="text" width={60} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="text" width={80} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="text" width={70} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Skeleton variant="circular" width={24} height={24} />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : sortedGuests.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center">
                                                <Typography variant="body2" color="text.secondary" py={4}>
                                                    No guests found with the selected filters
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sortedGuests.map((guest, index) => (
                                            <React.Fragment key={guest.rowKey}>
                                                <TableRow hover>
                                                    <TableCell>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => toggleGuestExpansion(guest.rowKey, e)}
                                                        >
                                                            {expandedGuests.has(guest.rowKey) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {index + 1}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Avatar
                                                                src={guest.imageS3Key ? ensureDirectS3Url(getS3Url(guest.imageS3Key)) : undefined}
                                                                alt={guest.guestName}
                                                                sx={{ width: 40, height: 40 }}
                                                            >
                                                                {guest.guestName.charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle2" fontWeight="bold">
                                                                    {guest.guestName}
                                                                </Typography>
                                                                {guest.socialAccounts && guest.socialAccounts.length > 0 && (
                                                                    <Box display="flex" gap={0.5} mt={0.5}>
                                                                        {guest.socialAccounts.slice(0, 3).map((social: { platform: string; handle: string }, idx: number) => (
                                                                            <Chip
                                                                                key={idx}
                                                                                label={`${social.platform}: @${social.handle}`}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                sx={{ fontSize: '0.7rem' }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {guest.program ? (
                                                            <Chip
                                                                avatar={
                                                                    guest.program.imageS3Key ? (
                                                                        <Avatar
                                                                            src={getProgramImageUrl(guest.program)}
                                                                            alt={guest.program.name}
                                                                        />
                                                                    ) : undefined
                                                                }
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        {guest.program.name}
                                                                        {guest.program.role === 'host' && (
                                                                            <Chip
                                                                                label="Host"
                                                                                size="small"
                                                                                color="success"
                                                                                sx={{
                                                                                    height: 16,
                                                                                    fontSize: '0.65rem',
                                                                                    '& .MuiChip-label': { px: 0.5 }
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                }
                                                                size="small"
                                                                color={guest.program.role === 'host' ? 'success' : 'primary'}
                                                                variant="outlined"
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                No program assigned
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {guest.appearances}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                                            {formatNumber(guest.totalViews)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {formatNumber(guest.avgViews)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2" color="success.main" fontWeight="bold">
                                                            {guest.engagementRate.toFixed(2)}%
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {guest.baselineAvgViews !== null && guest.baselineAvgViews !== undefined ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatNumber(guest.baselineAvgViews)}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="text.disabled">
                                                                —
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {guest.viewsImpact !== null && guest.viewsImpact !== undefined ? (
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                sx={{
                                                                    color: guest.viewsImpact > 0
                                                                        ? 'success.main'
                                                                        : guest.viewsImpact < 0
                                                                            ? 'error.main'
                                                                            : 'text.secondary'
                                                                }}
                                                            >
                                                                {guest.viewsImpact > 0 ? '+' : ''}{guest.viewsImpact.toFixed(1)}%
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="text.disabled">
                                                                —
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleGuestMenuOpen(e, { _id: guest._id, guestName: guest.guestName })}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                                {/* Expandable content row */}
                                                <TableRow>
                                                    <TableCell colSpan={11} sx={{ py: 0, borderBottom: expandedGuests.has(guest.rowKey) ? undefined : 'none' }}>
                                                        <Collapse in={expandedGuests.has(guest.rowKey)} timeout="auto" unmountOnExit>
                                                            <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                                                                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                                                    Content featuring {guest.guestName}
                                                                </Typography>
                                                                {guest.topContent && guest.topContent.length > 0 ? (
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                        {guest.topContent.map((content: { _id: string; title: string; publishedAt: string; viewsAtDay7?: number; views?: number }) => (
                                                                            <Box
                                                                                key={content._id}
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'space-between',
                                                                                    p: 1.5,
                                                                                    bgcolor: 'background.paper',
                                                                                    borderRadius: 1,
                                                                                    border: '1px solid',
                                                                                    borderColor: 'divider',
                                                                                }}
                                                                            >
                                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                    <Typography variant="body2" fontWeight="medium" noWrap>
                                                                                        {content.title}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {new Date(content.publishedAt).toLocaleDateString('es-CL', {
                                                                                            year: 'numeric',
                                                                                            month: 'short',
                                                                                            day: 'numeric'
                                                                                        })}
                                                                                    </Typography>
                                                                                </Box>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 2 }}>
                                                                                    <Box sx={{ textAlign: 'right' }}>
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            Day 7
                                                                                        </Typography>
                                                                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                                                                            {formatNumber(content.viewsAtDay7 || content.views || 0)}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                    <Box sx={{ textAlign: 'right' }}>
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            Current
                                                                                        </Typography>
                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                            {formatNumber(content.views || 0)}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        No content available
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}


            {/* Brands Tab Content */}
            {activeTab === 2 && (
                <>
                    {/* Sub-tabs for Categories and Brands */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs value={brandsSubTab} onChange={(_e, newValue) => setBrandsSubTab(newValue)}>
                            <Tab label="Categories" />
                            <Tab label="Brands" />
                        </Tabs>
                    </Box>

                    {/* Categories Sub-tab */}
                    {brandsSubTab === 0 && (
                        <>
                            {categoryRankingsLoading ? (
                                <Box py={4} display="flex" justifyContent="center">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    {/* Export Button */}
                                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<DownloadIcon />}
                                            onClick={() => {
                                                const csvData: any[] = [];

                                                filteredCategoryRankings.forEach((ranking: any, categoryIndex: number) => {
                                                    // Add category summary row
                                                    csvData.push({
                                                        'Categoría': BRAND_CATEGORY_LABELS[ranking.category] || ranking.category,
                                                        'Marca': 'TOTAL CATEGORÍA',
                                                        'Programas': ranking.categoryTotals?.totalPrograms || 0,
                                                        'Episodios': ranking.categoryTotals?.totalEpisodeCount || 0,
                                                        'Menciones': ranking.categoryTotals?.totalPaidMentions || 0,
                                                        'Vistas Totales': ranking.categoryTotals?.totalViews || 0,
                                                        'Vistas (7 Días)': ranking.categoryTotals?.totalAccumulatedViews || 0,
                                                        'Vistas por Marca': ranking.categoryTotals?.brandCount > 0
                                                            ? Math.round((ranking.categoryTotals?.totalAccumulatedViews || 0) / ranking.categoryTotals.brandCount)
                                                            : 0,
                                                        'Número de Marcas': ranking.categoryTotals?.brandCount || 0,
                                                    });

                                                    // Add individual brand rows
                                                    (ranking.allBrands || []).forEach((brand: any) => {
                                                        csvData.push({
                                                            'Categoría': BRAND_CATEGORY_LABELS[ranking.category] || ranking.category,
                                                            'Marca': brand.name,
                                                            'Programas': brand.programCount || 0,
                                                            'Episodios': brand.totalContentCount || 0,
                                                            'Menciones': brand.totalPaidMentions || 0,
                                                            'Vistas Totales': brand.totalViews || 0,
                                                            'Vistas (7 Días)': brand.totalAccumulatedViews || 0,
                                                            'Vistas por Marca': brand.totalAccumulatedViews || 0,
                                                            'Número de Marcas': '',
                                                        });
                                                    });

                                                    // Add empty row between categories
                                                    if (categoryIndex < filteredCategoryRankings.length - 1) {
                                                        csvData.push({
                                                            'Categoría': '',
                                                            'Marca': '',
                                                            'Programas': '',
                                                            'Episodios': '',
                                                            'Menciones': '',
                                                            'Vistas Totales': '',
                                                            'Vistas (7 Días)': '',
                                                            'Vistas por Marca': '',
                                                            'Número de Marcas': '',
                                                        });
                                                    }
                                                });

                                                const filterInfo = getFilterInfo();
                                                if (brandSearchQuery.trim()) filterInfo.push(`search-${brandSearchQuery.trim().replace(/\s+/g, '-')}`);

                                                const csvContent = convertToCSV(csvData);
                                                const filename = createTimestampedFilename(`brand-categories-analysis${filterInfo.length > 0 ? `-${filterInfo.join('-')}` : ''}`);
                                                downloadCSV(csvContent, filename);
                                            }}
                                        >
                                            Export CSV
                                        </Button>
                                    </Box>

                                    {/* Brand Search Filter */}
                                    <Card sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Search Brand"
                                                        placeholder="Search by brand name..."
                                                        value={brandSearchQuery}
                                                        onChange={(e) => setBrandSearchQuery(e.target.value)}
                                                    />
                                                </Grid>
                                                {brandSearchQuery.trim() && (
                                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Mostrando {filteredCategoryRankings.length} categor{filteredCategoryRankings.length !== 1 ? 'ías' : 'ía'} coincidiendo con "{brandSearchQuery.trim()}"
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </CardContent>
                                    </Card>

                                    {/* Category Rankings */}
                                    <Card sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom fontWeight="bold">
                                                📊 Category Rankings
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Métricas totales agregadas de todas las marcas por categoría. Ordenado por {categoryRankingsOrderBy === 'brandCount' ? 'número de marcas' : categoryRankingsOrderBy === 'programCount' ? 'número de programas' : categoryRankingsOrderBy === 'totalEpisodeCount' ? 'episodios' : categoryRankingsOrderBy === 'totalPaidMentions' ? 'menciones' : categoryRankingsOrderBy === 'totalViews' ? 'vistas totales' : categoryRankingsOrderBy === 'totalAccumulatedViews' ? 'vistas (7 días)' : 'número de marcas'} ({categoryRankingsOrder === 'asc' ? 'ascendente' : 'descendente'}). Haz clic en los encabezados de columna para ordenar.
                                            </Typography>
                                            {filteredCategoryRankings.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary" py={2}>
                                                    No category rankings available
                                                </Typography>
                                            ) : (
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ width: 50 }} />
                                                                <TableCell><strong>Categoría</strong></TableCell>
                                                                <TableCell align="center">
                                                                    <TableSortLabel
                                                                        active={categoryRankingsOrderBy === 'brandCount'}
                                                                        direction={categoryRankingsOrderBy === 'brandCount' ? categoryRankingsOrder : 'asc'}
                                                                        onClick={() => handleCategoryRankingsRequestSort('brandCount')}
                                                                    >
                                                                        Brands
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <TableSortLabel
                                                                        active={categoryRankingsOrderBy === 'programCount'}
                                                                        direction={categoryRankingsOrderBy === 'programCount' ? categoryRankingsOrder : 'asc'}
                                                                        onClick={() => handleCategoryRankingsRequestSort('programCount')}
                                                                    >
                                                                        Programs
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <TableSortLabel
                                                                        active={categoryRankingsOrderBy === 'totalEpisodeCount'}
                                                                        direction={categoryRankingsOrderBy === 'totalEpisodeCount' ? categoryRankingsOrder : 'asc'}
                                                                        onClick={() => handleCategoryRankingsRequestSort('totalEpisodeCount')}
                                                                    >
                                                                        Episodios
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <TableSortLabel
                                                                        active={categoryRankingsOrderBy === 'totalPaidMentions'}
                                                                        direction={categoryRankingsOrderBy === 'totalPaidMentions' ? categoryRankingsOrder : 'asc'}
                                                                        onClick={() => handleCategoryRankingsRequestSort('totalPaidMentions')}
                                                                    >
                                                                        Paid Mentions
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <TableSortLabel
                                                                        active={categoryRankingsOrderBy === 'totalViews'}
                                                                        direction={categoryRankingsOrderBy === 'totalViews' ? categoryRankingsOrder : 'asc'}
                                                                        onClick={() => handleCategoryRankingsRequestSort('totalViews')}
                                                                    >
                                                                        Total Views
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <TableSortLabel
                                                                        active={categoryRankingsOrderBy === 'totalAccumulatedViews'}
                                                                        direction={categoryRankingsOrderBy === 'totalAccumulatedViews' ? categoryRankingsOrder : 'asc'}
                                                                        onClick={() => handleCategoryRankingsRequestSort('totalAccumulatedViews')}
                                                                    >
                                                                        Views (7 Days)
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {filteredCategoryRankings.map(({ category, categoryTotals, allBrands }: any) => {
                                                                const isCategoryExpanded = expandedCategories.has(category);

                                                                return (
                                                                    <React.Fragment key={category}>
                                                                        <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                                                                            <TableCell>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={(e) => toggleCategoryExpansion(category, e)}
                                                                                >
                                                                                    {isCategoryExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                                </IconButton>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Typography variant="body2" fontWeight="bold">
                                                                                    {BRAND_CATEGORY_LABELS[category] || category}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <Typography variant="body2" fontWeight="bold">
                                                                                    {categoryTotals?.brandCount || 0}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <Typography variant="body2" color="primary" fontWeight="bold">
                                                                                    {categoryTotals?.totalPrograms || 0}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <Typography variant="body2" fontWeight="bold">
                                                                                    {categoryTotals?.totalEpisodeCount || 0}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <Typography variant="body2" color="success.main" fontWeight="bold">
                                                                                    {categoryTotals?.totalPaidMentions || 0}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <Typography variant="body2" fontWeight="bold">
                                                                                    {formatNumber(categoryTotals?.totalViews || 0)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <Typography variant="body2" color="info.main" fontWeight="bold">
                                                                                    {formatNumber(categoryTotals?.totalAccumulatedViews || 0)}
                                                                                </Typography>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                        <TableRow>
                                                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                                                                <Collapse in={isCategoryExpanded} timeout="auto" unmountOnExit>
                                                                                    <Box sx={{ margin: 2 }}>
                                                                                        <Typography variant="h6" gutterBottom>
                                                                                            Marcas en "{BRAND_CATEGORY_LABELS[category] || category}"
                                                                                        </Typography>
                                                                                        {allBrands && allBrands.length > 0 ? (
                                                                                            <TableContainer>
                                                                                                <Table size="small">
                                                                                                    <TableHead>
                                                                                                        <TableRow>
                                                                                                            <TableCell sx={{ width: 50 }} />
                                                                                                            <TableCell><strong>Brand</strong></TableCell>
                                                                                                            <TableCell align="center"><strong>Programs</strong></TableCell>
                                                                                                            <TableCell align="center"><strong>Episodios</strong></TableCell>
                                                                                                            <TableCell align="center"><strong>Paid Mentions</strong></TableCell>
                                                                                                            <TableCell align="center"><strong>Total Views</strong></TableCell>
                                                                                                            <TableCell align="center"><strong>Views (7 Days)</strong></TableCell>
                                                                                                            <TableCell align="center"><strong>Actions</strong></TableCell>
                                                                                                        </TableRow>
                                                                                                    </TableHead>
                                                                                                    <TableBody>
                                                                                                        {allBrands.map((brand: any) => {
                                                                                                            const categoryBrandKey = `${category}::${brand._id}`;
                                                                                                            const isCategoryBrandExpanded = expandedCategoryBrands.has(categoryBrandKey);
                                                                                                            const fullBrand = brandsData?.brands?.find(
                                                                                                                (b: any) => String(b._id) === String(brand._id),
                                                                                                            ) as any;
                                                                                                            const displayName = brand.name;
                                                                                                            const brandId = brand._id;

                                                                                                            return (
                                                                                                                <React.Fragment key={brand._id}>
                                                                                                                    <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                                                                                                                        <TableCell>
                                                                                                                            <IconButton
                                                                                                                                size="small"
                                                                                                                                onClick={(e) => toggleCategoryBrandExpansion(category, brandId, e)}
                                                                                                                            >
                                                                                                                                {isCategoryBrandExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                                                                            </IconButton>
                                                                                                                        </TableCell>
                                                                                                                        <TableCell>
                                                                                                                            <Box display="flex" alignItems="center" gap={1.5}>
                                                                                                                                <Avatar
                                                                                                                                    src={brand.brandS3Key ? ensureDirectS3Url(getS3Url(brand.brandS3Key)) : undefined}
                                                                                                                                    sx={{ width: 32, height: 32 }}
                                                                                                                                >
                                                                                                                                    {!brand.brandS3Key && brand.name?.charAt(0).toUpperCase()}
                                                                                                                                </Avatar>
                                                                                                                                <Typography variant="body2" fontWeight="bold">
                                                                                                                                    {brand.name}
                                                                                                                                </Typography>
                                                                                                                            </Box>
                                                                                                                        </TableCell>
                                                                                                                        <TableCell align="center">
                                                                                                                            <Typography variant="body2" color="primary" fontWeight="bold">
                                                                                                                                {brand.programCount || 0}
                                                                                                                            </Typography>
                                                                                                                        </TableCell>
                                                                                                                        <TableCell align="center">
                                                                                                                            <Typography variant="body2" fontWeight="bold">
                                                                                                                                {brand.totalContentCount || 0}
                                                                                                                            </Typography>
                                                                                                                        </TableCell>
                                                                                                                        <TableCell align="center">
                                                                                                                            <Typography variant="body2" color="success.main" fontWeight="bold">
                                                                                                                                {brand.totalPaidMentions || 0}
                                                                                                                            </Typography>
                                                                                                                        </TableCell>
                                                                                                                        <TableCell align="center">
                                                                                                                            <Typography variant="body2" fontWeight="bold">
                                                                                                                                {formatNumber(brand.totalViews || 0)}
                                                                                                                            </Typography>
                                                                                                                        </TableCell>
                                                                                                                        <TableCell align="center">
                                                                                                                            <Typography variant="body2" color="info.main" fontWeight="bold">
                                                                                                                                {formatNumber(brand.totalAccumulatedViews || 0)}
                                                                                                                            </Typography>
                                                                                                                        </TableCell>
                                                                                                                        <TableCell align="center">
                                                                                                                            <IconButton
                                                                                                                                size="small"
                                                                                                                                onClick={(e) => handleCategoryChangeClick(e, brand._id, brand.name)}
                                                                                                                            >
                                                                                                                                <MoreVertIcon fontSize="small" />
                                                                                                                            </IconButton>
                                                                                                                        </TableCell>
                                                                                                                    </TableRow>
                                                                                                                    <TableRow>
                                                                                                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                                                                                                            <Collapse in={isCategoryBrandExpanded} timeout="auto" unmountOnExit>
                                                                                                                                <Box sx={{ margin: 2 }}>
                                                                                                                                    {brandsLoading && !fullBrand ? (
                                                                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                                                                            Cargando detalle…
                                                                                                                                        </Typography>
                                                                                                                                    ) : !fullBrand ? (
                                                                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                                                                            No se encontró el detalle
                                                                                                                                        </Typography>
                                                                                                                                    ) : (
                                                                                                                                        <>
                                                                                                                                            <Typography variant="h6" gutterBottom>
                                                                                                                                                Podcast Channels for &quot;{fullBrand.brandName || fullBrand.name || displayName}&quot;
                                                                                                                                            </Typography>
                                                                                                                                            {(fullBrand.channels || []).map((channel: any) => {
                                                                                                                                                const channelKey = `${brandId}-${channel.podcastId}`;
                                                                                                                                                const isChannelExpanded = expandedBrandChannels.has(channelKey);

                                                                                                                                                return (
                                                                                                                                                    <Box key={channel.podcastId} sx={{ mb: 2 }}>
                                                                                                                                                        <Box
                                                                                                                                                            display="flex"
                                                                                                                                                            alignItems="center"
                                                                                                                                                            justifyContent="space-between"
                                                                                                                                                            sx={{
                                                                                                                                                                p: 2,
                                                                                                                                                                bgcolor: 'background.default',
                                                                                                                                                                borderRadius: 1,
                                                                                                                                                                cursor: 'pointer',
                                                                                                                                                                '&:hover': { bgcolor: 'action.hover' },
                                                                                                                                                            }}
                                                                                                                                                            onClick={(e) => toggleBrandChannelExpansion(brandId, channel.podcastId, e)}
                                                                                                                                                        >
                                                                                                                                                            <Box display="flex" alignItems="center" gap={2}>
                                                                                                                                                                <IconButton size="small">
                                                                                                                                                                    {isChannelExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                                                                                                                </IconButton>
                                                                                                                                                                <Typography variant="subtitle2" fontWeight="bold">
                                                                                                                                                                    {channel.podcastTitle}
                                                                                                                                                                </Typography>
                                                                                                                                                            </Box>
                                                                                                                                                            <Box display="flex" gap={4}>
                                                                                                                                                                <Box>
                                                                                                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                                                                                                        Programs
                                                                                                                                                                    </Typography>
                                                                                                                                                                    <Typography variant="body2" fontWeight="bold" color="primary">
                                                                                                                                                                        {channel.programCount || 0}
                                                                                                                                                                    </Typography>
                                                                                                                                                                </Box>
                                                                                                                                                                <Box>
                                                                                                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                                                                                                        Mentions
                                                                                                                                                                    </Typography>
                                                                                                                                                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                                                                                                                                                        {channel.totalPaidMentions || 0}
                                                                                                                                                                    </Typography>
                                                                                                                                                                </Box>
                                                                                                                                                                <Box>
                                                                                                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                                                                                                        Views
                                                                                                                                                                    </Typography>
                                                                                                                                                                    <Typography variant="body2" fontWeight="bold" color="text.primary">
                                                                                                                                                                        {formatNumber(channel.totalViews || 0)}
                                                                                                                                                                    </Typography>
                                                                                                                                                                </Box>
                                                                                                                                                                <Box>
                                                                                                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                                                                                                        Views (7d)
                                                                                                                                                                    </Typography>
                                                                                                                                                                    <Typography variant="body2" fontWeight="bold" color="info.main">
                                                                                                                                                                        {formatNumber(channel.totalAccumulatedViews || 0)}
                                                                                                                                                                    </Typography>
                                                                                                                                                                </Box>
                                                                                                                                                            </Box>
                                                                                                                                                        </Box>
                                                                                                                                                                                                                                                                                                                <Collapse in={isChannelExpanded} timeout="auto" unmountOnExit>
                                                                                                                                                            <BrandChannelMentionsPanel
                                                                                                                                                                brandId={brandId}
                                                                                                                                                                brandName={fullBrand.brandName || fullBrand.name || displayName}
                                                                                                                                                                podcastId={String(channel.podcastId)}
                                                                                                                                                                podcastTitle={channel.podcastTitle}
                                                                                                                                                                week={buildDateParams().week}
                                                                                                                                                                year={buildDateParams().year}
                                                                                                                                                                startDate={buildDateParams().startDate}
                                                                                                                                                                endDate={buildDateParams().endDate}
                                                                                                                                                                taxonomy={podcastTaxonomy}
                                                                                                                                                                onDeleteMention={handleDeleteMention}
                                                                                                                                                                deletePending={deleteMentionMutation.isPending}
                                                                                                                                                            />
                                                                                                                                                        </Collapse>
                                                                                                                                                    </Box>
                                                                                                                                                );
                                                                                                                                            })}
                                                                                                                                        </>
                                                                                                                                    )}
                                                                                                                                </Box>
                                                                                                                            </Collapse>
                                                                                                                        </TableCell>
                                                                                                                    </TableRow>
                                                                                                                </React.Fragment>
                                                                                                            );
                                                                                                        })}
                                                                                                    </TableBody>
                                                                                                </Table>
                                                                                            </TableContainer>
                                                                                        ) : (
                                                                                            <Typography variant="body2" color="text.secondary">
                                                                                                No brands found in this category
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                </Collapse>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )}
                                        </CardContent>
                                    </Card>
                                </>
                            )}
                        </>
                    )}

                    {/* Brands Sub-tab */}
                    {brandsSubTab === 1 && (
                        <>
                            {/* Export Button */}
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => {
                                        const csvData = sortedBrands.map((brand, index) => ({
                                            Rank: index + 1,
                                            'Brand Name': brand.brandName || brand.name,
                                            Category: (() => {
                                                const key = brand.brandCategory || brand.category;
                                                return key ? (BRAND_CATEGORY_LABELS[key] || key) : 'N/A';
                                            })(),
                                            'Total Programs': brand.totalPrograms || brand.programCount,
                                            'Paid Mentions': brand.totalPaidMentions,
                                            'Total Views': brand.totalViews,
                                            'Views (7 Days)': brand.totalAccumulatedViews,
                                        }));

                                        const filterInfo = getFilterInfo();
                                        if (selectedBrandCategory !== 'all') filterInfo.push(`category-${selectedBrandCategory}`);

                                        const csvContent = convertToCSV(csvData);
                                        const filename = createTimestampedFilename(`brands-analysis${filterInfo.length > 0 ? `-${filterInfo.join('-')}` : ''}`);
                                        downloadCSV(csvContent, filename);
                                    }}
                                >
                                    Export CSV
                                </Button>
                            </Box>
                            {brandsError ? (
                                <Alert severity="error">Failed to load brands analytics data</Alert>
                            ) : (
                                <>
                                    {/* Summary Cards */}
                                    <Grid container spacing={3} sx={{ mb: 4 }}>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Card>
                                                <CardContent>
                                                    <Typography variant="h6" color="primary">
                                                        {brandsData?.totalBrands || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Brands with Paid Mentions
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Card>
                                                <CardContent>
                                                    <Typography variant="h6" color="secondary">
                                                        {brandsData?.totalPrograms || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Programs with Brands
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Card>
                                                <CardContent>
                                                    <Typography variant="h6" color="success.main">
                                                        {brandsData?.totalPaidMentions || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Total Paid Mentions
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Card>
                                                <CardContent>
                                                    <Typography variant="h6" color="info.main">
                                                        {totals.totalPrograms}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Total Programs
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Card>
                                                <CardContent>
                                                    <Typography variant="h6" color="info.main">
                                                        {totals.totalContent}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Total Episodes
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Card>
                                                <CardContent>
                                                    <Typography variant="h6" color="info.main">
                                                        {totals.totalDurationMinutes >= 60
                                                            ? `${(totals.totalDurationMinutes / 60).toFixed(1)}h`
                                                            : `${totals.totalDurationMinutes}m`
                                                        }
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Total Time Analyzed
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    {/* Brand Category Filter */}
                                    <Card sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Search Brand"
                                                        placeholder="Search by brand name..."
                                                        value={brandSearchQuery}
                                                        onChange={(e) => setBrandSearchQuery(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Categoría de Marca</InputLabel>
                                                        <Select
                                                            value={selectedBrandCategory}
                                                            label="Brand Category"
                                                            onChange={(e) => setSelectedBrandCategory(e.target.value)}
                                                        >
                                                            <MenuItem value="all">Todas las Categorías</MenuItem>
                                                            {(brandCategoriesData?.categories || []).map((category: string) => (
                                                                <MenuItem key={category} value={category}>
                                                                    {BRAND_CATEGORY_LABELS[category] || category}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                {(selectedBrandCategory !== 'all' || brandSearchQuery.trim()) && (
                                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Mostrando {sortedBrands.length} marca{sortedBrands.length !== 1 ? 's' : ''}
                                                            {selectedBrandCategory !== 'all' && ` en "${BRAND_CATEGORY_LABELS[selectedBrandCategory] || selectedBrandCategory}"`}
                                                            {brandSearchQuery.trim() && ` coincidiendo con "${brandSearchQuery.trim()}"`}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </CardContent>
                                    </Card>

                                    {/* Brands Table */}
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: 50 }} />
                                                    <TableCell align="center">#</TableCell>
                                                    <TableCell>
                                                        <TableSortLabel
                                                            active={brandOrderBy === 'name'}
                                                            direction={brandOrderBy === 'name' ? brandOrder : 'asc'}
                                                            onClick={() => handleBrandRequestSort('name')}
                                                        >
                                                            Brand
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="center">Category</TableCell>
                                                    <TableCell align="center">
                                                        <TableSortLabel
                                                            active={brandOrderBy === 'programCount'}
                                                            direction={brandOrderBy === 'programCount' ? brandOrder : 'asc'}
                                                            onClick={() => handleBrandRequestSort('programCount')}
                                                        >
                                                            Programs
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <TableSortLabel
                                                            active={brandOrderBy === 'totalPaidMentions'}
                                                            direction={brandOrderBy === 'totalPaidMentions' ? brandOrder : 'asc'}
                                                            onClick={() => handleBrandRequestSort('totalPaidMentions')}
                                                        >
                                                            Paid Mentions
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <TableSortLabel
                                                            active={brandOrderBy === 'totalViews'}
                                                            direction={brandOrderBy === 'totalViews' ? brandOrder : 'asc'}
                                                            onClick={() => handleBrandRequestSort('totalViews')}
                                                        >
                                                            Total Views
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <TableSortLabel
                                                            active={brandOrderBy === 'totalAccumulatedViews'}
                                                            direction={brandOrderBy === 'totalAccumulatedViews' ? brandOrder : 'asc'}
                                                            onClick={() => handleBrandRequestSort('totalAccumulatedViews')}
                                                        >
                                                            Views (7 Days)
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="center">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {brandsLoading ? (
                                                    Array.from({ length: 10 }).map((_, index) => (
                                                        <TableRow key={`skeleton-${index}`}>
                                                            <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                                                            <TableCell align="center"><Skeleton variant="text" width={30} /></TableCell>
                                                            <TableCell>
                                                                <Box display="flex" alignItems="center" gap={2}>
                                                                    <Skeleton variant="circular" width={40} height={40} />
                                                                    <Skeleton variant="text" width={200} />
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="center"><Skeleton variant="text" width={100} /></TableCell>
                                                            <TableCell align="center"><Skeleton variant="text" width={60} /></TableCell>
                                                            <TableCell align="center"><Skeleton variant="text" width={60} /></TableCell>
                                                            <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                                                            <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                                                            <TableCell align="center"><Skeleton variant="circular" width={24} height={24} /></TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : sortedBrands.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={9} align="center">
                                                            <Typography variant="body2" color="text.secondary" py={4}>
                                                                No brands found with paid mentions
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    sortedBrands.map((brand: any, index) => {
                                                        const brandId = brand._id;
                                                        const isBrandExpanded = expandedBrands.has(brandId);

                                                        return (
                                                            <React.Fragment key={brandId}>
                                                                <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                                                                    <TableCell>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => toggleBrandExpansion(brandId, e)}
                                                                        >
                                                                            {isBrandExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                        </IconButton>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {index + 1}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box display="flex" alignItems="center" gap={2}>
                                                                            <Avatar
                                                                                src={brand.brandS3Key || brand.s3Key ? ensureDirectS3Url(getS3Url(brand.brandS3Key || brand.s3Key)) : undefined}
                                                                                sx={{ width: 40, height: 40 }}
                                                                            >
                                                                                {!brand.brandS3Key && !brand.s3Key && (brand.brandName || brand.name).charAt(0).toUpperCase()}
                                                                            </Avatar>
                                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                                {brand.brandName || brand.name}
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        {(() => {
                                                                            const categoryKey = brand.brandCategory || brand.category;
                                                                            return (
                                                                                <Typography variant="body2" color="text.secondary">
                                                                                    {categoryKey
                                                                                        ? (BRAND_CATEGORY_LABELS[categoryKey] || categoryKey)
                                                                                        : 'N/A'}
                                                                                </Typography>
                                                                            );
                                                                        })()}
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                                                            {brand.totalPrograms || brand.programCount || 0}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                                                            {brand.totalPaidMentions}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Typography variant="body2" fontWeight="bold" color="text.primary">
                                                                            {formatNumber(brand.totalViews || 0)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Typography variant="body2" fontWeight="bold" color="info.main">
                                                                            {formatNumber(brand.totalAccumulatedViews || 0)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => handleBrandMergeMenuClick(e, brand)}
                                                                        >
                                                                            <MoreVertIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                                <TableRow>
                                                                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                                                        <Collapse in={isBrandExpanded} timeout="auto" unmountOnExit>
                                                                            <Box sx={{ margin: 2 }}>
                                                                                <Typography variant="h6" gutterBottom>
                                                                                    Podcast Channels for "{brand.brandName || brand.name}"
                                                                                </Typography>
                                                                                {(brand.channels || []).map((channel: any) => {
                                                                                    const channelKey = `${brandId}-${channel.podcastId}`;
                                                                                    const isChannelExpanded = expandedBrandChannels.has(channelKey);

                                                                                    return (
                                                                                        <Box key={channel.podcastId} sx={{ mb: 2 }}>
                                                                                            <Box
                                                                                                display="flex"
                                                                                                alignItems="center"
                                                                                                justifyContent="space-between"
                                                                                                sx={{
                                                                                                    p: 2,
                                                                                                    bgcolor: 'background.default',
                                                                                                    borderRadius: 1,
                                                                                                    cursor: 'pointer',
                                                                                                    '&:hover': { bgcolor: 'action.hover' }
                                                                                                }}
                                                                                                onClick={(e) => toggleBrandChannelExpansion(brandId, channel.podcastId, e)}
                                                                                            >
                                                                                                <Box display="flex" alignItems="center" gap={2}>
                                                                                                    <IconButton size="small">
                                                                                                        {isChannelExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                                                    </IconButton>
                                                                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                                                                        {channel.podcastTitle}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                                <Box display="flex" gap={4}>
                                                                                                    <Box>
                                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                                            Programs
                                                                                                        </Typography>
                                                                                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                                                                                            {channel.programCount || 0}
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                    <Box>
                                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                                            Mentions
                                                                                                        </Typography>
                                                                                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                                                                                            {channel.totalPaidMentions || 0}
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                    <Box>
                                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                                            Views
                                                                                                        </Typography>
                                                                                                        <Typography variant="body2" fontWeight="bold" color="text.primary">
                                                                                                            {formatNumber(channel.totalViews || 0)}
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                    <Box>
                                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                                            Views (7d)
                                                                                                        </Typography>
                                                                                                        <Typography variant="body2" fontWeight="bold" color="info.main">
                                                                                                            {formatNumber(channel.totalAccumulatedViews || 0)}
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                </Box>
                                                                                            </Box>
                                                                                                                                                                                        <Collapse in={isChannelExpanded} timeout="auto" unmountOnExit>
                                                                                                <BrandChannelMentionsPanel
                                                                                                    brandId={brandId}
                                                                                                    brandName={brand.brandName || brand.name}
                                                                                                    podcastId={String(channel.podcastId)}
                                                                                                    podcastTitle={channel.podcastTitle}
                                                                                                    week={buildDateParams().week}
                                                                                                    year={buildDateParams().year}
                                                                                                    startDate={buildDateParams().startDate}
                                                                                                    endDate={buildDateParams().endDate}
                                                                                                    taxonomy={podcastTaxonomy}
                                                                                                    onDeleteMention={handleDeleteMention}
                                                                                                    deletePending={deleteMentionMutation.isPending}
                                                                                                />
                                                                                            </Collapse>
                                                                                        </Box>
                                                                                    );
                                                                                })}
                                                                            </Box>
                                                                        </Collapse>
                                                                    </TableCell>
                                                                </TableRow>
                                                            </React.Fragment>
                                                        );
                                                    })
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Time Series Tab Content */}
            {activeTab === 4 && (
                <>
                    {/* Export Button, Granularity Toggle, and Filter */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Granularity</InputLabel>
                                <Select
                                    value={timeSeriesGranularity}
                                    label="Granularity"
                                    onChange={(e) => setTimeSeriesGranularity(e.target.value as 'daily' | 'monthly')}
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 250 }}>
                                <InputLabel>Program</InputLabel>
                                <Select
                                    value={selectedProgramId}
                                    label="Program"
                                    onChange={(e) => setSelectedProgramId(e.target.value)}
                                >
                                    <MenuItem value="all">All Programs</MenuItem>
                                    {sortedPrograms.map((program) => (
                                        <MenuItem key={program._id} value={program._id}>
                                            {program.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={excludeSingleProgramBrands}
                                        onChange={(e) => setExcludeSingleProgramBrands(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Exclude brands in only 1 program"
                            />
                            <Tooltip title="Only include programs that have at least one content snapshot in every day/month of the selected period (same-store comparison)">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={consistentProgramsOnly}
                                            onChange={(e) => setConsistentProgramsOnly(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Consistent programs only"
                                />
                            </Tooltip>
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                                const timeSeries = timeSeriesData?.timeSeries || [];

                                // Get selected program info if a program is selected
                                const selectedProgram = selectedProgramId !== 'all'
                                    ? sortedPrograms.find(p => p._id === selectedProgramId)
                                    : null;

                                const podcastName = selectedProgram
                                    ? selectedProgram.podcast.title
                                    : (selectedPodcastId !== 'all'
                                        ? podcasts.find(p => p._id === selectedPodcastId)?.title || 'All Podcasts'
                                        : 'All Podcasts');

                                const programName = selectedProgram
                                    ? selectedProgram.name
                                    : 'All Programs';

                                const csvData = timeSeries.map((item: any) => ({
                                    Date: new Date(item.date).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: timeSeriesGranularity === 'daily' ? 'numeric' : undefined,
                                        timeZone: 'America/Santiago'
                                    }),
                                    'Podcast': podcastName,
                                    'Program': programName,
                                    'Programs Count': item.programsCount || 0,
                                    'Podcasts Count': item.podcastsCount || 0,
                                    'Content Count': item.contentCount,
                                    'Total Views': item.totalViews,
                                    'Avg Views': item.avgViews.toFixed(2),
                                    'Total Interactions': item.totalInteractions,
                                    'Avg Interactions': item.avgInteractions.toFixed(2),
                                    'Paid Brands': item.paidBrandsCount,
                                    'Engagement Rate': `${item.engagementRate.toFixed(2)}%`,
                                }));

                                const filterInfo = getFilterInfo();
                                filterInfo.push(`granularity-${timeSeriesGranularity}`);

                                const csvContent = convertToCSV(csvData);
                                const filename = createTimestampedFilename(`time-series${filterInfo.length > 0 ? `-${filterInfo.join('-')}` : ''}`);
                                downloadCSV(csvContent, filename);
                            }}
                        >
                            Export CSV
                        </Button>
                    </Box>

                    {timeSeriesError ? (
                        <Alert severity="error">Failed to load time-series analytics data</Alert>
                    ) : (
                        <>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Date filter = content published in range. Rows = snapshot dates; values = views as of that date from daily snapshots.
                            </Typography>
                            {/* Summary Cards */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Card>
                                        <CardContent>
                                            <Tooltip title="Content with a snapshot on the busiest day in the selected range (each row = one day/month).">
                                                <Typography variant="h6" color="primary" component="span" sx={{ cursor: 'help' }}>
                                                    {timeSeriesData?.timeSeries?.length
                                                        ? Math.max(...timeSeriesData.timeSeries.map((item: any) => item.contentCount || 0), 0)
                                                        : 0}
                                                </Typography>
                                            </Tooltip>
                                            <Typography variant="body2" color="text.secondary">
                                                Peak content (single {timeSeriesGranularity === 'daily' ? 'day' : 'month'})
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="secondary">
                                                {formatNumber(timeSeriesData?.timeSeries?.reduce((sum: number, item: any) => sum + item.totalViews, 0) || 0)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Views
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ color: '#2196F3' }}>
                                                {formatNumber(timeSeriesData?.timeSeries?.reduce((sum: number, item: any) => sum + item.totalInteractions, 0) || 0)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Interactions
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="info.main">
                                                {timeSeriesData?.timeSeries?.length || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {timeSeriesGranularity === 'daily' ? 'Days' : 'Months'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Time Series Table */}
                            {timeSeriesLoading ? (
                                <Box>
                                    <Skeleton variant="rectangular" height={400} />
                                </Box>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell align="center">Programs</TableCell>
                                                <TableCell align="center">Podcasts</TableCell>
                                                <TableCell align="center">Content</TableCell>
                                                <TableCell align="center">Total Views</TableCell>
                                                <TableCell align="center">Avg Views</TableCell>
                                                <TableCell align="center">Total Interactions</TableCell>
                                                <TableCell align="center">Avg Interactions</TableCell>
                                                <TableCell align="center">Paid Brands</TableCell>
                                                <TableCell align="center">Engagement %</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {timeSeriesData?.timeSeries?.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={10} align="center">
                                                        <Typography variant="body2" color="text.secondary" py={4}>
                                                            No time-series data found for the selected filters
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                timeSeriesData?.timeSeries?.map((item: any, index: number) => (
                                                    <TableRow key={index} hover>
                                                        <TableCell>
                                                            {new Date(item.date).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: timeSeriesGranularity === 'daily' ? 'numeric' : undefined,
                                                                timeZone: 'America/Santiago'
                                                            })}
                                                        </TableCell>
                                                        <TableCell align="center">{item.programsCount || 0}</TableCell>
                                                        <TableCell align="center">{item.podcastsCount || 0}</TableCell>
                                                        <TableCell align="center">{item.contentCount}</TableCell>
                                                        <TableCell align="center">{formatNumber(item.totalViews)}</TableCell>
                                                        <TableCell align="center">{item.avgViews.toFixed(2)}</TableCell>
                                                        <TableCell align="center">{formatNumber(item.totalInteractions)}</TableCell>
                                                        <TableCell align="center">{item.avgInteractions.toFixed(2)}</TableCell>
                                                        <TableCell align="center">{item.paidBrandsCount}</TableCell>
                                                        <TableCell align="center">{item.engagementRate.toFixed(2)}%</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {/* Line Chart */}
                            {timeSeriesData?.timeSeries && timeSeriesData.timeSeries.length > 0 && (
                                <Box sx={{ mt: 4 }}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                                                <Typography variant="h6">
                                                    Trends Over Time
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                    {[
                                                        { key: 'Content Count', color: '#1976d2', label: 'Content' },
                                                        { key: 'Total Views', color: '#2e7d32', label: 'Views' },
                                                        { key: 'Total Interactions', color: '#ed6c02', label: 'Interactions' },
                                                        { key: 'Paid Brands', color: '#9c27b0', label: 'Paid Brands' },
                                                        { key: 'Engagement Rate', color: '#d32f2f', label: 'Engagement' },
                                                    ].map((metric) => (
                                                        <FormControlLabel
                                                            key={metric.key}
                                                            control={
                                                                <Switch
                                                                    checked={visibleMetrics.has(metric.key)}
                                                                    onChange={(e) => {
                                                                        const newVisible = new Set(visibleMetrics);
                                                                        if (e.target.checked) {
                                                                            newVisible.add(metric.key);
                                                                        } else {
                                                                            newVisible.delete(metric.key);
                                                                        }
                                                                        setVisibleMetrics(newVisible);
                                                                    }}
                                                                    size="small"
                                                                />
                                                            }
                                                            label={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Box
                                                                        sx={{
                                                                            width: 12,
                                                                            height: 12,
                                                                            backgroundColor: metric.color,
                                                                            borderRadius: '2px'
                                                                        }}
                                                                    />
                                                                    <Typography variant="body2">{metric.label}</Typography>
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                            <Box sx={{ width: '100%', height: 400, mt: 2 }}>
                                                <ResponsiveContainer>
                                                    <LineChart
                                                        data={timeSeriesData.timeSeries.map((item: any) => ({
                                                            date: new Date(item.date).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: timeSeriesGranularity === 'daily' ? 'numeric' : undefined,
                                                                timeZone: 'America/Santiago'
                                                            }),
                                                            'Content Count': item.contentCount,
                                                            'Total Views': item.totalViews,
                                                            'Total Interactions': item.totalInteractions,
                                                            'Paid Brands': item.paidBrandsCount,
                                                            'Engagement Rate': item.engagementRate,
                                                        }))}
                                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                        <XAxis
                                                            dataKey="date"
                                                            tick={{ fontSize: 12 }}
                                                            angle={timeSeriesGranularity === 'daily' ? -45 : 0}
                                                            textAnchor={timeSeriesGranularity === 'daily' ? 'end' : 'middle'}
                                                            height={timeSeriesGranularity === 'daily' ? 80 : 40}
                                                        />
                                                        <YAxis
                                                            yAxisId="left"
                                                            tick={{ fontSize: 12 }}
                                                            tickFormatter={(value) => formatNumber(value)}
                                                        />
                                                        <YAxis
                                                            yAxisId="right"
                                                            orientation="right"
                                                            tick={{ fontSize: 12 }}
                                                            label={{ value: 'Engagement Rate (%)', angle: -90, position: 'insideRight' }}
                                                        />
                                                        <RechartsTooltip
                                                            formatter={(value, name) => {
                                                                if (name === 'Engagement Rate') {
                                                                    const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
                                                                    return [`${numValue.toFixed(2)}%`, name || ''];
                                                                }
                                                                if (name === 'Total Views' || name === 'Total Interactions') {
                                                                    const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
                                                                    return [formatNumber(numValue), name || ''];
                                                                }
                                                                return [value, name || ''];
                                                            }}
                                                        />
                                                        <Legend />
                                                        {visibleMetrics.has('Content Count') && (
                                                            <Line
                                                                yAxisId="left"
                                                                type="monotone"
                                                                dataKey="Content Count"
                                                                stroke="#1976d2"
                                                                strokeWidth={2}
                                                                dot={{ r: 4 }}
                                                                activeDot={{ r: 6 }}
                                                            />
                                                        )}
                                                        {visibleMetrics.has('Total Views') && (
                                                            <Line
                                                                yAxisId="left"
                                                                type="monotone"
                                                                dataKey="Total Views"
                                                                stroke="#2e7d32"
                                                                strokeWidth={2}
                                                                dot={{ r: 4 }}
                                                                activeDot={{ r: 6 }}
                                                            />
                                                        )}
                                                        {visibleMetrics.has('Total Interactions') && (
                                                            <Line
                                                                yAxisId="left"
                                                                type="monotone"
                                                                dataKey="Total Interactions"
                                                                stroke="#ed6c02"
                                                                strokeWidth={2}
                                                                dot={{ r: 4 }}
                                                                activeDot={{ r: 6 }}
                                                            />
                                                        )}
                                                        {visibleMetrics.has('Paid Brands') && (
                                                            <Line
                                                                yAxisId="left"
                                                                type="monotone"
                                                                dataKey="Paid Brands"
                                                                stroke="#9c27b0"
                                                                strokeWidth={2}
                                                                dot={{ r: 4 }}
                                                                activeDot={{ r: 6 }}
                                                            />
                                                        )}
                                                        {visibleMetrics.has('Engagement Rate') && (
                                                            <Line
                                                                yAxisId="right"
                                                                type="monotone"
                                                                dataKey="Engagement Rate"
                                                                stroke="#d32f2f"
                                                                strokeWidth={2}
                                                                dot={{ r: 4 }}
                                                                activeDot={{ r: 6 }}
                                                            />
                                                        )}
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Top Content Tab Content */}
            {activeTab === 3 && (
                <>
                    {/* Search, Pagination, Export */}
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search by title, program name, or podcast..."
                            value={topContentSearch}
                            onChange={(e) => {
                                setTopContentSearch(e.target.value);
                                setTopContentPage(1);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'action.active' }}>
                                        <SearchIcon fontSize="small" />
                                    </Box>
                                ),
                            }}
                            sx={{ minWidth: 260 }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={topContentPage <= 1}
                                onClick={() => setTopContentPage((p) => Math.max(1, p - 1))}
                            >
                                Prev
                            </Button>
                            <Typography variant="body2" sx={{ px: 1.5, alignSelf: 'center' }}>
                                Page {topContentPage} of {topContentTotalPages}
                                {topContentTotal > 0 && ` (${topContentTotal} total)`}
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={topContentPage >= topContentTotalPages}
                                onClick={() => setTopContentPage((p) => Math.min(topContentTotalPages, p + 1))}
                            >
                                Next
                            </Button>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => {
                                    const topContent = topContentData?.content || [];
                                    const csvData = topContent.map((content: any, index: number) => ({
                                        Rank: content.ranking ?? index + 1,
                                        Title: content.title,
                                        Program: content.programName || 'N/A',
                                        Podcast: content.podcastTitle || 'N/A',
                                        'Published Date': content.publishedAt ? new Date(content.publishedAt).toLocaleDateString() : 'N/A',
                                        Views: content.views || 0,
                                        Likes: content.likes || 0,
                                        Comments: content.comments || 0,
                                        Interactions: (content.likes || 0) + (content.comments || 0),
                                        'Engagement Rate': content.views ? `${(((content.likes || 0) + (content.comments || 0)) / content.views * 100).toFixed(2)}%` : '0%',
                                        URL: content.url || 'N/A',
                                    }));

                                    const filterInfo = getFilterInfo();
                                    const csvContent = convertToCSV(csvData);
                                    const filename = createTimestampedFilename(`top-content${filterInfo.length > 0 ? `-${filterInfo.join('-')}` : ''}`);
                                    downloadCSV(csvContent, filename);
                                }}
                            >
                                Export CSV
                            </Button>
                        </Box>
                    </Box>
                    {topContentError ? (
                        <Alert severity="error">Failed to load top content data</Alert>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="primary">
                                                {topContentData?.total || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Top Content Items
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="success.main">
                                                {formatNumber(topContentData?.content?.reduce((sum, item) => sum + item.views, 0) || 0)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Views
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ color: '#2196F3' }}>
                                                {formatNumber(topContentData?.content?.reduce((sum, item) => sum + item.interactions, 0) || 0)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Interactions
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" color="secondary">
                                                {topContentData?.content?.length ? Math.round(topContentData.content.reduce((sum, item) => sum + item.views, 0) / topContentData.content.length) : 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Avg Views
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Top Content Table */}
                            {topContentLoading ? (
                                <Box>
                                    <Skeleton variant="rectangular" height={400} />
                                </Box>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="center" sx={{ width: 50 }}>#</TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={topContentOrderBy === 'title'}
                                                        direction={topContentOrderBy === 'title' ? topContentOrder : 'asc'}
                                                        onClick={() => {
                                                            setTopContentOrderBy('title');
                                                            setTopContentOrder(topContentOrderBy === 'title' && topContentOrder === 'asc' ? 'desc' : 'asc');
                                                        }}
                                                    >
                                                        Content Title
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>Program</TableCell>
                                                <TableCell>Podcast</TableCell>
                                                <TableCell align="center">Source</TableCell>
                                                <TableCell align="right">
                                                    <TableSortLabel
                                                        active={topContentOrderBy === 'views'}
                                                        direction={topContentOrderBy === 'views' ? topContentOrder : 'desc'}
                                                        onClick={() => {
                                                            setTopContentOrderBy('views');
                                                            setTopContentOrder(topContentOrderBy === 'views' && topContentOrder === 'desc' ? 'asc' : 'desc');
                                                        }}
                                                    >
                                                        Views
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TableSortLabel
                                                        active={topContentOrderBy === 'likes'}
                                                        direction={topContentOrderBy === 'likes' ? topContentOrder : 'desc'}
                                                        onClick={() => {
                                                            setTopContentOrderBy('likes');
                                                            setTopContentOrder(topContentOrderBy === 'likes' && topContentOrder === 'desc' ? 'asc' : 'desc');
                                                        }}
                                                    >
                                                        Likes
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TableSortLabel
                                                        active={topContentOrderBy === 'comments'}
                                                        direction={topContentOrderBy === 'comments' ? topContentOrder : 'desc'}
                                                        onClick={() => {
                                                            setTopContentOrderBy('comments');
                                                            setTopContentOrder(topContentOrderBy === 'comments' && topContentOrder === 'desc' ? 'asc' : 'desc');
                                                        }}
                                                    >
                                                        Comments
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TableSortLabel
                                                        active={topContentOrderBy === 'interactions'}
                                                        direction={topContentOrderBy === 'interactions' ? topContentOrder : 'desc'}
                                                        onClick={() => {
                                                            setTopContentOrderBy('interactions');
                                                            setTopContentOrder(topContentOrderBy === 'interactions' && topContentOrder === 'desc' ? 'asc' : 'desc');
                                                        }}
                                                    >
                                                        Interactions
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TableSortLabel
                                                        active={topContentOrderBy === 'engagement'}
                                                        direction={topContentOrderBy === 'engagement' ? topContentOrder : 'desc'}
                                                        onClick={() => {
                                                            setTopContentOrderBy('engagement');
                                                            setTopContentOrder(topContentOrderBy === 'engagement' && topContentOrder === 'desc' ? 'asc' : 'desc');
                                                        }}
                                                    >
                                                        Engagement %
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>Published</TableCell>
                                                <TableCell>Link</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(() => {
                                                const content = topContentData?.content || [];
                                                const sorted = [...content].sort((a, b) => {
                                                    let aVal: any = a[topContentOrderBy as keyof typeof a];
                                                    let bVal: any = b[topContentOrderBy as keyof typeof b];

                                                    if (topContentOrderBy === 'title') {
                                                        aVal = (aVal || '').toLowerCase();
                                                        bVal = (bVal || '').toLowerCase();
                                                    } else if (topContentOrderBy === 'engagement') {
                                                        aVal = (a as any).views > 0 ? ((a as any).likes + (a as any).comments) / (a as any).views : 0;
                                                        bVal = (b as any).views > 0 ? ((b as any).likes + (b as any).comments) / (b as any).views : 0;
                                                    }

                                                    if (topContentOrder === 'asc') {
                                                        return aVal > bVal ? 1 : -1;
                                                    } else {
                                                        return aVal < bVal ? 1 : -1;
                                                    }
                                                });

                                                const TOP_CONTENT_COLUMN_COUNT = 12;

                                                return sorted.map((item) => {
                                                    const sourceIcon = item.source === 'youtube' ? '📺' :
                                                        item.source === 'instagram' ? '📷' :
                                                            item.source === 'tiktok' ? '🎵' :
                                                                item.source === 'spotify' ? '🎧' :
                                                                    item.source === 'facebook' ? '👥' : '📱';
                                                    const isSelected = selectedTopContentId === item._id;

                                                    return (
                                                        <React.Fragment key={item._id}>
                                                            <TableRow
                                                                hover
                                                                onClick={() => setSelectedTopContentId((prev) => (prev === item._id ? null : item._id))}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    bgcolor: isSelected ? 'action.selected' : undefined,
                                                                }}
                                                            >
                                                                <TableCell align="center">
                                                                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                                        {item.ranking}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                                                        {item.title}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {item.programName && (
                                                                        <Chip
                                                                            label={item.programName}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{ maxWidth: 200 }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                                        {item.podcastTitle}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Typography variant="body2">
                                                                        {sourceIcon} {item.source}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {formatNumber(item.views)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">{formatNumber(item.likes)}</TableCell>
                                                                <TableCell align="right">{formatNumber(item.comments)}</TableCell>
                                                                <TableCell align="right">{formatNumber(item.interactions)}</TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2">
                                                                        {item.views && item.views > 0
                                                                            ? `${(((item.likes || 0) + (item.comments || 0)) / item.views * 100).toFixed(2)}%`
                                                                            : 'N/A'}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : 'N/A'}
                                                                </TableCell>
                                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                                    {item.url && (
                                                                        <Link href={item.url} target="_blank" rel="noopener noreferrer">
                                                                            <YouTubeIcon fontSize="small" />
                                                                        </Link>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                            {isSelected && (
                                                                <TableRow>
                                                                    <TableCell colSpan={TOP_CONTENT_COLUMN_COUNT} sx={{ py: 0, px: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', verticalAlign: 'top' }}>
                                                                        <Box sx={{ py: 2 }}>
                                                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                                                <TrendingUpIcon fontSize="small" color="primary" />
                                                                                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                                                                    Daily growth: {item.title}
                                                                                </Typography>
                                                                            </Box>
                                                                            {selectedContentMetricsLoading ? (
                                                                                <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                    <CircularProgress />
                                                                                </Box>
                                                                            ) : selectedContentMetricsError ? (
                                                                                <Alert severity="error">Failed to load daily metrics for this content.</Alert>
                                                                            ) : selectedContentMetrics?.dailyMetrics && selectedContentMetrics.dailyMetrics.length > 0 ? (
                                                                                <Box sx={{ width: '100%', height: 320 }}>
                                                                                    <ResponsiveContainer>
                                                                                        <LineChart
                                                                                            data={[...selectedContentMetrics.dailyMetrics]
                                                                                                .sort((a, b) => new Date(a.collectedAt).getTime() - new Date(b.collectedAt).getTime())
                                                                                                .map((m) => ({
                                                                                                    date: new Date(m.collectedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit', timeZone: 'America/Santiago' }),
                                                                                                    Views: m.views,
                                                                                                    Likes: m.likes,
                                                                                                    Comments: m.comments,
                                                                                                }))}
                                                                                            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                                                                                        >
                                                                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                                                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                                                                            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} />
                                                                                            <RechartsTooltip formatter={(value, name) => [formatNumber(Number(value)), name]} />
                                                                                            <Legend />
                                                                                            <Line type="monotone" dataKey="Views" stroke="#2e7d32" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                                                                            <Line type="monotone" dataKey="Likes" stroke="#ed6c02" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                                                                            <Line type="monotone" dataKey="Comments" stroke="#9c27b0" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                                                                        </LineChart>
                                                                                    </ResponsiveContainer>
                                                                                </Box>
                                                                            ) : (
                                                                                <Typography variant="body2" color="text.secondary">
                                                                                    No daily metrics available for this content.
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                });
                                            })()}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Copa Mundial (FIFA classification) Tab */}
            {activeTab === 5 && (
                <ProgramAnalysisFifaTab
                    enabled={activeTab === 5}
                    dateParams={buildDateParams()}
                    podcastId={selectedPodcastId !== 'all' ? selectedPodcastId : undefined}
                />
            )}

            {/* Guest Action Menu */}
            <Menu
                anchorEl={guestMenuAnchor}
                open={Boolean(guestMenuAnchor)}
                onClose={handleGuestMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box sx={{ p: 2, minWidth: 300 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Assign {selectedGuestForAction?.guestName} as Host
                    </Typography>
                    <Autocomplete
                        size="small"
                        options={allPrograms}
                        getOptionLabel={(option) => option.name}
                        value={selectedProgramForHost}
                        onChange={(_, newValue) => setSelectedProgramForHost(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Program" placeholder="Type to search..." />
                        )}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <MenuItem onClick={handleGuestMenuClose} sx={{ borderRadius: 1 }}>
                            Cancel
                        </MenuItem>
                        <MenuItem
                            onClick={handleAssignAsHost}
                            disabled={!selectedProgramForHost || assignHostMutation.isPending}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                borderRadius: 1,
                                '&:hover': { bgcolor: 'primary.dark' },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                            }}
                        >
                            {assignHostMutation.isPending ? 'Assigning...' : 'Assign as Host'}
                        </MenuItem>
                    </Box>
                </Box>
            </Menu>

            {/* Brand Merge Menu */}
            <Menu
                anchorEl={brandMergeMenuAnchor?.element || null}
                open={Boolean(brandMergeMenuAnchor)}
                onClose={handleBrandMergeMenuClose}
            >
                <MenuItem onClick={handleBrandEditCategoryFromMenu}>
                    Edit category
                </MenuItem>
                <MenuItem onClick={handleBrandMergeDialogOpen}>
                    Migrate to another brand
                </MenuItem>
            </Menu>

            <BrandMergeDialog
                open={Boolean(mergeDialogBrand)}
                onClose={() => setMergeDialogBrand(null)}
                sourceBrand={mergeDialogBrand}
                onSuccess={(message) => setSnackbarMessage(message)}
                onError={(message) => setSnackbarMessage(message)}
            />

            {/* Category Change Menu */}
            <Menu
                anchorEl={categoryChangeAnchor?.element || null}
                open={Boolean(categoryChangeAnchor)}
                onClose={handleCategoryChangeClose}
            >
                <Box sx={{ p: 2, minWidth: 200 }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Cambiar Categoría para "{categoryChangeAnchor?.brandName}"
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
                        <InputLabel>Nueva Categoría</InputLabel>
                        <Select
                            value={selectedNewCategory}
                            label="Nueva Categoría"
                            onChange={(e) => setSelectedNewCategory(e.target.value)}
                        >
                            <MenuItem value="none">Sin Categoría</MenuItem>
                            {[...BRAND_CATEGORIES]
                                .sort((a, b) => {
                                    const labelA = BRAND_CATEGORY_LABELS[a] || a;
                                    const labelB = BRAND_CATEGORY_LABELS[b] || b;
                                    return labelA.localeCompare(labelB);
                                })
                                .map((category: string) => (
                                    <MenuItem key={category} value={category}>
                                        {BRAND_CATEGORY_LABELS[category] || category}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                        <Button size="small" onClick={handleCategoryChangeClose}>
                            Cancelar
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleCategoryChangeConfirm}
                            disabled={updateBrandCategoryMutation.isPending}
                        >
                            {updateBrandCategoryMutation.isPending ? 'Actualizando...' : 'Actualizar'}
                        </Button>
                    </Box>
                </Box>
            </Menu>

            {/* Snackbar for notifications */}
            <Snackbar
                open={Boolean(snackbarMessage)}
                autoHideDuration={4000}
                onClose={() => setSnackbarMessage(null)}
                message={snackbarMessage}
            />
        </Container>
    );
};

export default ProgramAnalysisReview;

